import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import DateRangePicker from '../components/DateRangePicker'
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles'
import useMediaQuery from '@mui/system/useMediaQuery'
import {
  Typography,
  Stack,
  Box,
  Chip,
  IconButton,
  Input,
  Avatar,
  Badge,
  Sheet,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Button,
  Tooltip,
  FormControl,
  FormLabel,
  Switch,
  Tabs,
  TabList,
  Tab,
  tabClasses,
  Select,
  Option,
  Menu,
  MenuItem,
  Checkbox,
  Modal,
  ModalDialog,
  Autocomplete,
  LinearProgress,
  CircularProgress,
} from '@mui/joy'
import {
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as ResolvedIcon,
  Schedule as PendingIcon,
  Error as OpenIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContactPage as ContactIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Replay as ReplayIcon,
  SwapHoriz as TransferIcon,
  Message as MessageIcon,
  AccessTime as AccessTimeIcon,
  CheckBox as CheckBoxIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AttachMoney as MoneyIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import api from '../services/api'
import axios from 'axios'
import { toast } from 'react-toastify'
import { settleWithConcurrency, motivoDeError } from '../utils/settleWithConcurrency'
import ContactDrawer from '../components/ContactDrawer'
import formatLastMessagePreview from '../utils/formatLastMessagePreview'
import { highlightText } from '../utils/highlightText'
import AcceptTicketModal from '../components/AcceptTicketModal'
import MessageInput, { type OptimisticMediaMessage } from '../components/MessageInput'
import socketService from '../services/socket'
import { useAuth } from '../hooks/useAuth'
import { useMessageFormatting } from '../hooks/useMessageFormatting'
import DateSeparator from '../components/Messages/DateSeparator'
import TikTokCommentBubble from '../components/Messages/TikTokCommentBubble'
import MessageContent from '../components/Messages/MessageContent'
import ChannelBadge from '../components/Messages/ChannelBadge'
import RoutingPolicySelector from '../components/Messages/RoutingPolicySelector'
import MetaWindowIndicator from '../components/Messages/MetaWindowIndicator'
import DispatchTimeline from '../components/Messages/DispatchTimeline'
import ConversationSearchBar from '../components/Messages/ConversationSearchBar'
import MediaLightbox from '../components/Messages/MediaLightbox'
import ForwardSelectionBar from '../components/ForwardSelectionBar'
import ForwardContactPicker from '../components/ForwardContactPicker'
import type { ForwardDestination } from '../components/ForwardContactPicker'
import { useThemeColors } from '../context/ThemeContext'
import type { Message } from '../types/Message'
import {
  displayContactName,
  displayContactSubtitle,
  contactAvatarInitial,
  mergeContactPreservingName,
} from '../utils/contactDisplay'

// URL del backend para medios
const BACKEND_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'https://appro.chateam.ws';

const dragEventHasFiles = (event: React.DragEvent<HTMLDivElement>) => (
  Array.from(event.dataTransfer.types || []).includes('Files')
)

const getChatBackgroundSx = (isDark: boolean) => {
  // Nuevo patrón de fondo del chat (imágenes PNG con transparencia):
  //  - claro → patron-fondo-claro.png sobre fondo BLANCO (#FFFFFF)
  //  - oscuro → patron-fondo-oscuro.png sobre fondo oscuro (#18191A)
  const pattern = isDark ? '/patron-fondo-oscuro.png' : '/patron-fondo-claro.png'

  return {
    backgroundColor: isDark ? '#18191A' : '#FFFFFF',
    backgroundImage: `url("${pattern}")`,
    // Patrón de iconos en mosaico (repetible). backgroundSize controla el
    // tamaño de los iconos; 'auto' mantiene la proporción del PNG.
    backgroundRepeat: 'repeat',
    // Iconos al doble de tamaño (620 -> 1240px). El PNG nativo es 3841px de
    // ancho, así que a 1240px sigue reduciéndose desde el original: sin pérdida
    // de calidad (se ve nítido).
    backgroundSize: '1240px auto',
    backgroundPosition: 'center top',
  }
}

const isRequestCanceled = (error: any) => (
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'AbortError'
)

interface Tag {
  id: number
  name: string
  color: string
  kanban?: number
}

interface Contact {
  id: number
  name: string
  number: string
  profilePicUrl?: string
  urlPicture?: string
  tags?: Tag[]
}

interface User {
  id: number
  name: string
  profile?: string
  whatsappId?: number | null
  queues?: Queue[]
}

interface Queue {
  id: number
  name: string
  color: string
}

interface Whatsapp {
  id: number
  name: string
  channel?: string
}

interface Ticket {
  id: number
  uuid: string
  status: string
  unreadMessages: number
  lastMessage?: string
  contactId: number
  userId?: number
  queueId?: number
  whatsappId?: number
  isGroup?: boolean
  channel?: string
  contact: Contact
  user?: User
  queue?: Queue
  whatsapp?: Whatsapp
  tags?: Tag[]
  messages: Message[]
  messagesPageNumber?: number
  messagesHasMore?: boolean
  createdAt: string
  updatedAt: string
  // Búsqueda en mensajes (Fase 1): nº de mensajes coincidentes y el más reciente
  matchCount?: number
  lastMatch?: { id: number; body: string; createdAt: string } | null
}

// Respuesta del endpoint POST /messages/ticket/:id/recover (backend reescrito).
interface RecoverTicketMessagesResponse {
  success: boolean
  ticketId: number
  ciphertextFound: number
  placeholder: {
    requested: number
    alreadyResolved: number
    alreadyRequested: number
    failed: number
    skipped: number
    expired: number
    exhausted: number
    quotaExceeded: number
    requests: Array<{ messageId: string; status: string; error?: string }>
  }
  history: {
    requested: boolean
    requestId?: string
    count?: number
    error?: string
  }
  cooldownSeconds: number
  // Resumen ya redactado en español por el backend, listo para mostrar al operador.
  message: string
}

// Payload de error que puede acompañar un 429 (cooldown/cuota) del endpoint de recuperación.
interface RecoverTicketMessagesErrorPayload {
  message?: string
  cooldownSeconds?: number
}

const DEFAULT_RECOVERY_COOLDOWN_SECONDS = 300
const RECOVERY_COOLDOWN_STORAGE_KEY = 'chateam:ticketRecoveryCooldownUntil'

// Lee el mapa ticketId -> timestamp(ms) de fin de cooldown persistido, descartando
// entradas ya vencidas para no arrastrar basura indefinidamente en localStorage.
function loadRecoveryCooldownMap(): Record<number, number> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(RECOVERY_COOLDOWN_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, number>
    const now = Date.now()
    const result: Record<number, number> = {}
    for (const [ticketIdKey, until] of Object.entries(parsed)) {
      const ticketId = Number(ticketIdKey)
      if (Number.isFinite(ticketId) && typeof until === 'number' && until > now) {
        result[ticketId] = until
      }
    }
    return result
  } catch {
    return {}
  }
}

// Ordena mensajes por fecha ascendente. Los mensajes nuevos ya llegan en orden
// (quedan igual), pero los RECUPERADOS son antiguos y deben insertarse en su
// lugar cronológico, no al final de la conversación.
function sortMessagesByCreatedAt(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
}

// Clave de identidad de un mensaje, usada para no pintar dos veces el mismo.
//
// Se usa SIEMPRE `id` (la clave primaria). Es el único campo que viene en las dos
// vías por las que llega un mensaje al chat.
//
// 2026-08-12: la primera versión priorizaba `wid` (el id de WhatsApp) por ser
// estable entre reintentos. Fue un error: `ListMessagesService` devuelve una lista
// explícita de atributos que NO incluye `wid`, así que el mismo mensaje producía
// dos claves distintas —`w:<wid>` al llegar por socket e `i:<id>` al recargarse
// desde el historial— y se veía DUPLICADO en pantalla hasta refrescar.
//
// Si algún día se añade `wid` a los atributos de ListMessagesService, seguirá
// siendo preferible `id`: es la PK, nunca falta y no cambia.
function messageIdentity(message: Message): string {
  return `i:${message.id}`
}

// Fusiona los mensajes que ya había en pantalla con los que devuelve el backend.
//
// 2026-08-12: antes el historial recién cargado REEMPLAZABA el array completo. Si
// entre la petición y la respuesta llegaba un mensaje por socket (o había un
// archivo subiéndose), desaparecía del chat hasta el siguiente evento. Ahora el
// servidor manda sobre los mensajes que sí devuelve, y se conservan los locales
// que él no incluye.
function mergeMessageLists(localMessages: Message[], serverMessages: Message[]): Message[] {
  if (localMessages.length === 0) return serverMessages
  if (serverMessages.length === 0) return localMessages

  const merged = new Map<string, Message>()
  // El backend es la fuente de verdad para todo lo que devuelve.
  serverMessages.forEach(message => merged.set(messageIdentity(message), message))
  // Se conserva lo que solo existe en cliente (socket reciente / envío en curso).
  localMessages.forEach(message => {
    const key = messageIdentity(message)
    if (!merged.has(key)) merged.set(key, message)
  })

  return sortMessagesByCreatedAt(Array.from(merged.values()))
}

// Construye un fragmento centrado en la coincidencia para que el término sea
// visible aunque el mensaje sea largo (la fila usa noWrap y trunca al final).
function getMatchSnippet(body: string, term: string, radius = 40): string {
  if (!body) return ''
  const t = term.trim()
  if (!t) return body
  const idx = body.toLowerCase().indexOf(t.toLowerCase())
  if (idx === -1) return body
  const start = Math.max(0, idx - radius)
  const end = Math.min(body.length, idx + t.length + radius)
  return (
    (start > 0 ? '…' : '') +
    body.slice(start, end) +
    (end < body.length ? '…' : '')
  )
}

export default function Tickets() {
  const { user } = useAuth()
  const location = useLocation()
  const { facebookTheme, facebookDesignTokens } = useThemeColors()
  const { mode, setMode } = useColorScheme()
  const isDark = mode === 'dark'

  // ── Modo chat ampliado ─────────────────────────────────────────────────────
  // En un celular de 360px la lista fija de 300px dejaba ~60px al chat, con lo
  // que era imposible escribir. `chatAmpliado` oculta la lista y le da el ancho
  // completo a la conversación. En móvil arranca activo en cuanto hay un ticket
  // abierto; en escritorio es opcional y se recuerda la preferencia.
  const esMovil = useMediaQuery('(max-width: 900px)')
  const [chatAmpliado, setChatAmpliado] = useState<boolean>(() => {
    try { return localStorage.getItem('ticketsChatAmpliado') === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem('ticketsChatAmpliado', chatAmpliado ? '1' : '0') } catch { /* modo privado */ }
  }, [chatAmpliado])


  const ticketSidebarWidth = esMovil ? '100%' : 300

  // Scrollbar delgado estilo Shadcn
  const thinScrollbarSx = {
    '&::-webkit-scrollbar': { width: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    },
    scrollbarWidth: 'thin' as const,
    scrollbarColor: isDark ? 'rgba(255,255,255,0.15) transparent' : 'rgba(0,0,0,0.15) transparent',
  }

  const { formatTime, formatDate, formatDateSeparator, formatTicketDate, shouldShowDateSeparator } = useMessageFormatting()

  const toggleTheme = () => {
    setMode(mode === 'light' ? 'dark' : 'light')
  }

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value)
    }, 300)
  }, [])

  const [tickets, setTickets] = useState<Ticket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)

  // En movil no caben lista y chat a la vez: al abrir una conversacion se pasa
  // al chat completo, y al cerrarla se vuelve a la lista.
  useEffect(() => {
    if (!esMovil) return
    setChatAmpliado(Boolean(selectedTicket))
  }, [esMovil, selectedTicket?.id])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('open')
  // Por defecto muestra SOLO mis tickets. El usuario puede activar el toggle "Mostrar todos"
  // si es admin/super o tiene allUserChat habilitado. Bug fix: antes iniciaba en true y
  // mostraba tickets de toda la empresa al cargar (2026-05-22).
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [recoveringMessages, setRecoveringMessages] = useState(false)
  // Cooldown de "Recuperar mensajes faltantes" por ticket (protección anti-abuso del
  // backend). Persistido en localStorage para que un refresh no salte la protección.
  const [recoveryCooldownUntil, setRecoveryCooldownUntil] = useState<Record<number, number>>(() => loadRecoveryCooldownMap())
  // Forzador de re-render mientras haya un cooldown activo, para que el botón se
  // reactive y el tooltip actualice los minutos restantes sin esperar otro evento.
  const [recoveryCooldownTick, setRecoveryCooldownTick] = useState(0)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [whatsappFilter, setWhatsappFilter] = useState<string>('')
  // Filtro por CANAL ACTIVO del ticket (no crea conversaciones separadas):
  // 'all' | 'whatsapp' (Baileys) | 'meta' | 'facebook' | 'instagram' | 'telegram'
  const [channelFilter, setChannelFilter] = useState<string>('all')
  const [userFilter, setUserFilter] = useState<string>('')
  const [queueFilter, setQueueFilter] = useState<string>('')
  /** Filtro de bandeja por etiqueta ('' = todas). El backend busca la etiqueta
   *  tanto en el contacto como en la conversación. */
  const [tagFilter, setTagFilter] = useState<string>('')
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [searchMessages, setSearchMessages] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [queues, setQueues] = useState<Queue[]>([])
  const [whatsapps, setWhatsapps] = useState<Whatsapp[]>([])
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false)
  const [dragDropFiles, setDragDropFiles] = useState<File[]>([])
  const [isChatDraggingFiles, setIsChatDraggingFiles] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const preserveMessageScrollRef = useRef(false)
  const chatDragDepthRef = useRef(0)

  // ─── CREAR TICKET MANUAL ───
  const [showNewTicketModal, setShowNewTicketModal] = useState(false)
  const [newTicketContactSearch, setNewTicketContactSearch] = useState('')
  const [newTicketContacts, setNewTicketContacts] = useState<{ id: number; name: string; number: string }[]>([])
  const [newTicketContactId, setNewTicketContactId] = useState<number | null>(null)
  const [newTicketQueueId, setNewTicketQueueId] = useState<string>('')
  const [newTicketWhatsappId, setNewTicketWhatsappId] = useState<string>('')
  const [newTicketLoading, setNewTicketLoading] = useState(false)
  const [searchingNewTicketContacts, setSearchingNewTicketContacts] = useState(false)
  const contactSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Campaign Message (para mostrar banner de campaña publicitaria)
  const [campaignMessage, setCampaignMessage] = useState<any>(null)
  const [editingConversion, setEditingConversion] = useState(false)
  const [conversionNoteValue, setConversionNoteValue] = useState('')
  const [savingConversion, setSavingConversion] = useState(false)
  const [sendingPurchaseConversion, setSendingPurchaseConversion] = useState(false)
  const [purchaseConfirmModal, setPurchaseConfirmModal] = useState<{
    open: boolean
    campaignMessage: any | null
    value: number
  }>({ open: false, campaignMessage: null, value: 0 })

  // Asignación manual de campaña
  const [campaigns, setCampaigns] = useState<{
    id: string
    name: string
    status: string
    effective_status?: string
    activeAds?: number // Cantidad de anuncios activos (viene del backend)
    insights?: {
      impressions: number
      clicks: number
      spend: number
    }
  }[]>([])
  const [ads, setAds] = useState<{ id: string; name: string; status: string; creative?: any }[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('')
  const [selectedAdId, setSelectedAdId] = useState<string>('')
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [loadingAds, setLoadingAds] = useState(false)
  const [assigning, setAssigning] = useState(false)

  // Customer Origin (Origen de Cliente)
  const [customerOrigins, setCustomerOrigins] = useState<{
    id: number
    name: string
    color: string
    isActive: boolean
  }[]>([])
  const [loadingOrigins, setLoadingOrigins] = useState(false)

  // Contadores para las tabs
  const [openCount, setOpenCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [confirmarCierreMasivo, setConfirmarCierreMasivo] = useState(false)
  const [cerrandoTodos, setCerrandoTodos] = useState(false)
  const [closedCount, setClosedCount] = useState(0)
  const [groupCount, setGroupCount] = useState(0)

  // ─── EDICIÓN INLINE ───
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null)
  const [editingMessageBody, setEditingMessageBody] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)

  // ─── MEDIA LIGHTBOX ───
  const [lightboxData, setLightboxData] = useState<{
    src: string
    type: string
    currentIndex: number
    allMedia: Array<{ id: number; src: string; type: string }>
  } | null>(null)

  // ─── UI OPTIMISTA DE MEDIA (subida en progreso) ───
  // Mapa tempId -> porcentaje de subida (0-100) para los placeholders en vuelo.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})

  // Inserta los placeholders (preview local + "enviando") en la conversación abierta.
  const handleMediaSendStart = (placeholders: OptimisticMediaMessage[]) => {
    setSelectedTicket((prev) =>
      prev ? { ...prev, messages: [...prev.messages, ...(placeholders as unknown as Message[])] } : prev
    )
    setUploadProgress((prev) => {
      const next = { ...prev }
      placeholders.forEach((p) => { next[p.id] = 0 })
      return next
    })
  }

  // Actualiza el % de subida de los placeholders del lote.
  const handleMediaSendProgress = (tempIds: string[], percent: number) => {
    setUploadProgress((prev) => {
      const next = { ...prev }
      tempIds.forEach((id) => { next[id] = percent })
      return next
    })
  }

  // Retira los placeholders al terminar (éxito → el real ya llegó por socket; error → se descarta).
  const handleMediaSendEnd = (tempIds: string[]) => {
    const ids = new Set(tempIds)
    setSelectedTicket((prev) => {
      if (!prev) return prev
      prev.messages.forEach((m) => {
        const url = (m as { mediaUrl?: string }).mediaUrl
        if (ids.has(String(m.id)) && typeof url === 'string' && url.startsWith('blob:')) {
          URL.revokeObjectURL(url)
        }
      })
      return { ...prev, messages: prev.messages.filter((m) => !ids.has(String(m.id))) }
    })
    setUploadProgress((prev) => {
      const next = { ...prev }
      tempIds.forEach((id) => { delete next[id] })
      return next
    })
  }

  // ─── REPLY TO ───
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)

  // ─── BÚSQUEDA EN CONVERSACIÓN ───
  const [conversationSearchTerm, setConversationSearchTerm] = useState('')
  const [conversationSearchActive, setConversationSearchActive] = useState(false)

  // ─── SELECCIÓN MÚLTIPLE ───
  const [selectionMode, setSelectionMode] = useState<'forward' | 'delete' | null>(null)
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<number>>(new Set())
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [forwardLoading, setForwardLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // ─── PAGINACIÓN INFINITA ───
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false) // carga inicial de mensajes al abrir un ticket
  const ticketsListRef = useRef<HTMLDivElement>(null)
  const ticketsRef = useRef<Ticket[]>([])
  const selectedTicketRef = useRef<Ticket | null>(null)
  const fetchTicketsRef = useRef<(reset?: boolean, explicitPage?: number, silent?: boolean) => Promise<void>>(async () => {})
  const ticketDetailsFetchInFlightRef = useRef<Set<number>>(new Set())
  const ticketDetailsLastFetchAtRef = useRef<Map<number, number>>(new Map())
  const ticketListRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Ref sincronizada con `debouncedSearchTerm` para que los handlers socket
  // (cerrados sobre estado viejo) puedan saber si hay búsqueda activa sin
  // forzar re-suscripción de listeners.
  const searchActiveRef = useRef<boolean>(false)

  // ─── MENÚ DE ACCIONES ───
  const [messageActionMenu, setMessageActionMenu] = useState<{
    messageId: number
    anchorEl: HTMLElement | null
    fromMe: boolean
  } | null>(null)

  // ─── MENÚ DE OPCIONES DEL TICKET ───
  const [ticketMoreMenu, setTicketMoreMenu] = useState<HTMLElement | null>(null)
  const [showTransferModal, setShowTransferModal] = useState(false)
  // El modal "Transferir Ticket" reasigna el ticket a otro USUARIO/AGENTE (cambia ticket.userId),
  // no cambia el contacto. Las variables siguen el patrón de usuarios.
  const [searchUserQuery, setSearchUserQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedUser, setSelectedUser] = useState<any | null>(null)
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [transferring, setTransferring] = useState(false)

  // ─── ACEPTAR TICKET CON COLA OBLIGATORIA ───
  const [acceptQueueModalOpen, setAcceptQueueModalOpen] = useState(false)
  const [ticketToAccept, setTicketToAccept] = useState<Ticket | null>(null)
  const [acceptingTicket, setAcceptingTicket] = useState(false)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  useEffect(() => {
    ticketsRef.current = tickets
  }, [tickets])

  useEffect(() => {
    selectedTicketRef.current = selectedTicket
  }, [selectedTicket])

  const getTicketTimestamp = useCallback((ticket?: Partial<Ticket> | null) => {
    if (!ticket) return 0
    const rawValue = ticket.updatedAt || ticket.createdAt
    if (!rawValue) return 0

    const parsedValue = new Date(rawValue).getTime()
    return Number.isNaN(parsedValue) ? 0 : parsedValue
  }, [])

  const mergeTicketData = useCallback((
    previousTicket: Ticket | null | undefined,
    incomingTicket: Partial<Ticket> & { id: number }
  ): Ticket => {
    const fallbackDate = new Date().toISOString()
    const baseTicket = (previousTicket ?? {
      id: incomingTicket.id,
      uuid: incomingTicket.uuid || '',
      status: incomingTicket.status || 'pending',
      unreadMessages: incomingTicket.unreadMessages ?? 0,
      contactId: incomingTicket.contactId ?? incomingTicket.contact?.id ?? 0,
      contact: (incomingTicket.contact ?? {
        id: incomingTicket.contactId ?? 0,
        name: '',
        number: ''
      }) as Contact,
      messages: [],
      createdAt: incomingTicket.createdAt || fallbackDate,
      updatedAt: incomingTicket.updatedAt || incomingTicket.createdAt || fallbackDate
    }) as Ticket

    // Preserva el name humano del contacto previo si el entrante no trae uno válido.
    // Esto evita que eventos socket parciales (donde contact.name === contact.number)
    // pisen el name humano que tenía la BD/lista (bug visible: header del chat
    // mostraba número mientras la lista mostraba "GARCIA ANDRADE...").
    const mergedContact = incomingTicket.contact
      ? ({
          ...(mergeContactPreservingName(
            baseTicket.contact,
            incomingTicket.contact as Partial<Contact>
          ) ?? incomingTicket.contact),
          tags: incomingTicket.contact.tags ?? baseTicket.contact?.tags
        } as Contact)
      : baseTicket.contact

    const incomingMessages = Array.isArray(incomingTicket.messages)
      ? incomingTicket.messages
      : undefined
    const baseMessages = baseTicket.messages || []
    const isExplicitMessageLoad =
      incomingTicket.messagesPageNumber !== undefined ||
      incomingTicket.messagesHasMore !== undefined
    const shouldUseIncomingMessages =
      incomingMessages !== undefined &&
      (
        isExplicitMessageLoad ||
        incomingMessages.length > 0 ||
        baseMessages.length === 0
      )

    return {
      ...baseTicket,
      ...incomingTicket,
      contact: mergedContact,
      user: incomingTicket.user ?? baseTicket.user,
      queue: incomingTicket.queue ?? baseTicket.queue,
      whatsapp: incomingTicket.whatsapp ?? baseTicket.whatsapp,
      tags: incomingTicket.tags ?? baseTicket.tags,
      messages: shouldUseIncomingMessages ? incomingMessages! : baseMessages,
      messagesPageNumber: incomingTicket.messagesPageNumber ?? baseTicket.messagesPageNumber ?? 1,
      messagesHasMore: incomingTicket.messagesHasMore ?? baseTicket.messagesHasMore ?? false,
      createdAt: incomingTicket.createdAt ?? baseTicket.createdAt ?? fallbackDate,
      updatedAt: incomingTicket.updatedAt ?? baseTicket.updatedAt ?? incomingTicket.createdAt ?? fallbackDate
    }
  }, [])

  const normalizeTickets = useCallback((ticketList: Ticket[]) => {
    const ticketMap = new Map<number, Ticket>()

    ticketList.forEach(ticket => {
      if (!ticket?.id) return

      const previousTicket = ticketMap.get(ticket.id)
      ticketMap.set(ticket.id, mergeTicketData(previousTicket, ticket))
    })

    return Array.from(ticketMap.values()).sort(
      (leftTicket, rightTicket) => getTicketTimestamp(rightTicket) - getTicketTimestamp(leftTicket)
    )
  }, [getTicketTimestamp, mergeTicketData])

  const shouldDisplayTicket = useCallback((ticket: Ticket) => {
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'pending') {
        if (!['pending', 'lgpd'].includes(ticket.status)) return false
      } else if (ticket.status !== statusFilter) {
        return false
      }
    }

    if (whatsappFilter && ticket.whatsappId !== Number(whatsappFilter)) return false
    // Filtro por canal activo del ticket (coexistencia: Meta y Baileys son
    // transportes del MISMO WhatsApp → 'whatsapp' agrupa Baileys/whatsapp).
    if (channelFilter && channelFilter !== 'all') {
      const tChannel = (ticket as any).channel || 'whatsapp'
      if (channelFilter === 'whatsapp') {
        if (!['whatsapp', 'baileys'].includes(tChannel)) return false
      } else if (tChannel !== channelFilter) {
        return false
      }
    }
    if (userFilter && ticket.userId !== Number(userFilter)) return false
    if (queueFilter && ticket.queueId !== Number(queueFilter)) return false

    const ticketTimestamp = getTicketTimestamp(ticket)

    if (startDate) {
      const startTimestamp = new Date(`${startDate}T00:00:00`).getTime()
      if (ticketTimestamp < startTimestamp) return false
    }

    if (endDate) {
      const endTimestamp = new Date(`${endDate}T23:59:59.999`).getTime()
      if (ticketTimestamp > endTimestamp) return false
    }

    const currentUserRecord = users.find(item => item.id === user?.id)

    if (user?.profile === 'user') {
      if (currentUserRecord?.whatsappId && ticket.whatsappId && ticket.whatsappId !== currentUserRecord.whatsappId) {
        return false
      }

      const assignedQueueIds = new Set((currentUserRecord?.queues || []).map(queue => queue.id))
      if (ticket.queueId && assignedQueueIds.size > 0 && !assignedQueueIds.has(ticket.queueId)) {
        return false
      }
    }

    if (!showAll && user?.id) {
      if (statusFilter === 'open' && ticket.userId !== user.id) return false
      if (statusFilter === 'pending' && ticket.userId !== undefined && ticket.userId !== null && ticket.userId !== user.id) return false
      if (statusFilter === 'closed' && ticket.userId !== user.id) return false
    }

    return true
  }, [endDate, getTicketTimestamp, queueFilter, showAll, startDate, statusFilter, user?.id, user?.profile, userFilter, users, whatsappFilter, channelFilter])

  const upsertTicketInList = useCallback((ticketList: Ticket[], incomingTicket: Ticket) => {
    const ticketIndex = ticketList.findIndex(ticket => ticket.id === incomingTicket.id)
    const mergedTicket = mergeTicketData(ticketIndex >= 0 ? ticketList[ticketIndex] : null, incomingTicket)

    if (!shouldDisplayTicket(mergedTicket)) {
      return normalizeTickets(ticketList.filter(ticket => ticket.id !== incomingTicket.id))
    }

    if (ticketIndex >= 0) {
      const nextTicketList = [...ticketList]
      nextTicketList[ticketIndex] = mergedTicket
      return normalizeTickets(nextTicketList)
    }

    return normalizeTickets([mergedTicket, ...ticketList])
  }, [mergeTicketData, normalizeTickets, shouldDisplayTicket])

  const removeTicketFromList = useCallback((ticketList: Ticket[], ticketId: number) => {
    return normalizeTickets(ticketList.filter(ticket => ticket.id !== ticketId))
  }, [normalizeTickets])

  // Buscar usuarios/agentes para transferencia del ticket
  // (no se buscan contactos: la transferencia reasigna el responsable del ticket)
  const handleSearchUser = async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([])
      return
    }
    setSearchingUsers(true)
    try {
      const res = await api.get('/users', {
        params: { searchParam: query, pageNumber: 1 }
      })
      // El endpoint /users devuelve { users, count, hasMore }
      setSearchResults(res.data.users || res.data.records || [])
    } catch (error) {
      console.error('Error searching users:', error)
      setSearchResults([])
    } finally {
      setSearchingUsers(false)
    }
  }

  // Transferir ticket a otro usuario/agente (cambia ticket.userId)
  // IMPORTANTE: NO cambiamos el status del ticket. Transferir solo reasigna
  // el responsable. Si el ticket era `pending`, sigue `pending` y el nuevo
  // agente lo acepta cuando esté listo. Esto evita disparar la validación
  // `requireQueueOnAccept` que aplica solo a transiciones pending → open.
  const handleTransferTicket = async () => {
    if (!selectedTicket?.id || !selectedUser) return
    setTransferring(true)
    try {
      // Solo enviamos los campos que cambian: userId (y queueId si se eligió otra).
      // El status se preserva enviando el actual del ticket.
      const payload: { userId: number; status: string; queueId?: number } = {
        userId: selectedUser.id,
        status: selectedTicket.status,
      }

      // Preservar la cola actual si existe (nunca borrar la asignación)
      if (selectedTicket.queueId) {
        payload.queueId = selectedTicket.queueId
      }

      await api.put(`/tickets/${selectedTicket.id}`, payload)
      toast.success(`Ticket transferido a ${selectedUser.name}`)
      setShowTransferModal(false)
      setSelectedUser(null)
      setSearchUserQuery('')
      setSearchResults([])
      setTicketMoreMenu(null)
      // NO recargamos la lista: el socket `company-X-ticket` con action='update'
      // ya upsertea el ticket (con queue/user/whatsapp hidratados) sin perder el
      // scroll del operador. Solo refrescamos contadores.
      fetchTicketCounts()
    } catch (error: any) {
      console.error('Error transferring ticket:', error)
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Error al transferir el ticket')
    } finally {
      setTransferring(false)
    }
  }

  // Persiste el mapa de cooldowns en localStorage ante cualquier cambio, así un
  // refresh de página no permite saltarse la protección anti-abuso del backend.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(RECOVERY_COOLDOWN_STORAGE_KEY, JSON.stringify(recoveryCooldownUntil))
    } catch {
      // localStorage puede fallar (modo privado, cuota); no es crítico para el flujo.
    }
  }, [recoveryCooldownUntil])

  // Mientras exista algún cooldown activo, se re-renderiza periódicamente para
  // reactivar el botón y actualizar los minutos restantes del tooltip sin
  // depender de que llegue otro evento (socket, click, etc).
  useEffect(() => {
    const hasActiveCooldown = Object.values(recoveryCooldownUntil).some(until => until > Date.now())
    if (!hasActiveCooldown) return

    const timer = setTimeout(() => setRecoveryCooldownTick(tick => tick + 1), 15000)
    return () => clearTimeout(timer)
  }, [recoveryCooldownUntil, recoveryCooldownTick])

  const setTicketRecoveryCooldown = useCallback((ticketId: number, seconds: number) => {
    const safeSeconds = seconds && seconds > 0 ? seconds : DEFAULT_RECOVERY_COOLDOWN_SECONDS
    const now = Date.now()
    const until = now + safeSeconds * 1000
    setRecoveryCooldownUntil(prev => {
      // Se podan las entradas ya vencidas: sin esto el mapa crece una clave por
      // cada ticket recuperado y se reserializa entero en localStorage en cada
      // cambio (solo se limpiaba al recargar la página).
      const pruned: Record<number, number> = {}
      Object.entries(prev).forEach(([key, value]) => {
        if (value > now) pruned[Number(key)] = value
      })
      pruned[ticketId] = until
      return pruned
    })
  }, [])

  const getTicketRecoveryCooldownRemainingSeconds = useCallback((ticketId: number): number => {
    const until = recoveryCooldownUntil[ticketId]
    if (!until) return 0
    const remainingMs = until - Date.now()
    return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recoveryCooldownUntil, recoveryCooldownTick])

  const handleRecoverTicketMessages = async () => {
    const ticketId = selectedTicket?.id
    if (!ticketId || recoveringMessages) return
    if (getTicketRecoveryCooldownRemainingSeconds(ticketId) > 0) return

    setRecoveringMessages(true)
    try {
      // Sin `limit`: el backend aplica sus propios máximos por mecanismo
      // (10 placeholders para no exceder el timeout de axios, y hasta 50 de
      // historial, que es el tope que admite WhatsApp). Enviar un límite único
      // desde aquí recortaría innecesariamente la petición de historial.
      const { data } = await api.post<RecoverTicketMessagesResponse>(
        `/messages/ticket/${ticketId}/recover`,
        {}
      )

      const hasRecoverableWork = (data.placeholder?.requested ?? 0) > 0 || Boolean(data.history?.requested)
      const summary = data.message || 'Recuperación de mensajes procesada'

      if (hasRecoverableWork) {
        toast.success(summary)
      } else {
        toast.info(summary)
      }

      setTicketRecoveryCooldown(ticketId, data.cooldownSeconds)
    } catch (error) {
      if (axios.isAxiosError<RecoverTicketMessagesErrorPayload>(error)) {
        const status = error.response?.status
        const backendMessage = error.response?.data?.message

        if (status === 429) {
          // No es un fallo: es la protección anti-abuso (cooldown o cuota horaria).
          toast.warning(backendMessage || 'Debes esperar antes de volver a recuperar mensajes de este ticket')
          setTicketRecoveryCooldown(ticketId, error.response?.data?.cooldownSeconds ?? DEFAULT_RECOVERY_COOLDOWN_SECONDS)
        } else if (status === 503) {
          toast.error(backendMessage || 'La sesión de WhatsApp no está disponible en este momento')
        } else {
          toast.error(backendMessage || 'No se pudo iniciar la recuperación')
        }
      } else {
        toast.error('No se pudo iniciar la recuperación')
      }
    } finally {
      setRecoveringMessages(false)
      setTicketMoreMenu(null)
    }
  }

  // Función para obtener contadores de todos los estados
  const fetchTicketCounts = useCallback(async () => {
    try {
      const showAllParam = showAll ? 'true' : 'false'

      // Construir parámetros base con TODOS los filtros activos
      const baseParams = {
        showAll: showAllParam,
        limit: 0,
        pageNumber: 1,
        // Incluir todos los filtros activos para que el conteo sea preciso
        ...(whatsappFilter && { whatsapps: JSON.stringify([Number(whatsappFilter)]) }),
        ...(userFilter && { users: JSON.stringify([Number(userFilter)]) }),
        ...(queueFilter && { queueIds: JSON.stringify([Number(queueFilter)]) }),
        ...(tagFilter && { tags: JSON.stringify([Number(tagFilter)]) }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(searchMessages && { searchOnMessages: 'true' })
      }

      // Obtener contadores reales de cada status usando el endpoint /tickets con limit=0
      const [openRes, pendingRes, closedRes, groupRes] = await Promise.all([
        api.get('/tickets', { params: { ...baseParams, status: 'open' } }),
        api.get('/tickets', { params: { ...baseParams, status: 'pending' } }),
        api.get('/tickets', { params: { ...baseParams, status: 'closed' } }),
        api.get('/tickets', { params: { ...baseParams, status: 'group' } })
      ])

      setOpenCount(openRes.data.totalCount ?? openRes.data.count ?? 0)
      setPendingCount(pendingRes.data.totalCount ?? pendingRes.data.count ?? 0)
      setClosedCount(closedRes.data.totalCount ?? closedRes.data.count ?? 0)
      setGroupCount(groupRes.data.totalCount ?? groupRes.data.count ?? 0)
    } catch (error) {
      console.error('Error fetching ticket counts:', error)
    }
  }, [showAll, whatsappFilter, userFilter, queueFilter, tagFilter, startDate, endDate, searchMessages])

  // Cierre masivo de tickets del estado activo (solo Abiertos o Pendientes).
  // El backend responde 202 y sigue trabajando en segundo plano por lotes, asi
  // que aqui no se espera al final: el listado se va vaciando solo con los
  // eventos de socket que emite cada cierre.
  const cerrarTodosLosTickets = useCallback(async () => {
    setCerrandoTodos(true)
    try {
      const { data } = await api.post('/tickets/closeAll', { status: statusFilter })
      const total = data?.total ?? 0
      setConfirmarCierreMasivo(false)
      if (total === 0) {
        toast.info('No hay tickets que cerrar en este estado.')
      } else {
        toast.success(
          `Cerrando ${total} tickets en segundo plano. La lista se ira actualizando sola.`
        )
      }
    } catch (error: any) {
      const codigo = error?.response?.status
      if (codigo === 403) {
        toast.error('Solo un administrador puede cerrar tickets en bloque.')
      } else {
        toast.error(error?.response?.data?.error || 'No se pudo iniciar el cierre masivo.')
      }
    } finally {
      setCerrandoTodos(false)
    }
  }, [statusFilter])

  // Debounce fetchTicketCounts to avoid excessive API calls from socket events
  const fetchTicketCountsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncedFetchTicketCounts = useCallback(() => {
    if (fetchTicketCountsTimeoutRef.current) {
      clearTimeout(fetchTicketCountsTimeoutRef.current)
    }
    fetchTicketCountsTimeoutRef.current = setTimeout(() => {
      fetchTicketCounts()
    }, 800)
  }, [fetchTicketCounts])

  const scheduleTicketListRefresh = useCallback(() => {
    if (ticketListRefreshTimeoutRef.current) {
      clearTimeout(ticketListRefreshTimeoutRef.current)
    }

    ticketListRefreshTimeoutRef.current = setTimeout(() => {
      // silent=true → refresh "transparente" disparado por eventos socket.
      // No muestra "Cargando chats..." al operador (eso solo debe verlo quien
      // explícitamente cambió un filtro o búsqueda).
      void fetchTicketsRef.current(true, undefined, true)
    }, 250)
  }, [])

  useEffect(() => {
    return () => {
      if (ticketListRefreshTimeoutRef.current) {
        clearTimeout(ticketListRefreshTimeoutRef.current)
      }
    }
  }, [])

  // Resetear paginación cuando cambian los filtros o el término de búsqueda
  useEffect(() => {
    setPageNumber(1)
    setHasMore(true)
    // Mantener ref sincronizada con búsqueda activa (la consultan los handlers
    // socket para evitar refrescos que pisarían los resultados de la búsqueda).
    searchActiveRef.current = debouncedSearchTerm.trim().length > 0
    fetchTickets(true)
    // Actualizar contadores con debounce para evitar demasiadas llamadas
    debouncedFetchTicketCounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, showAll, startDate, endDate, whatsappFilter, userFilter, queueFilter, tagFilter, searchMessages, debouncedSearchTerm])

  // Obtener contadores al inicio y cuando cambie showAll
  useEffect(() => {
    fetchTicketCounts()
  }, [fetchTicketCounts])

  useEffect(() => {
    fetchFilterOptions()
  }, [])

  useEffect(() => {
    if (preserveMessageScrollRef.current) return
    scrollToBottom()
  }, [selectedTicket?.messages])

  // Cargar mensaje de campaña cuando cambie el ticket seleccionado
  useEffect(() => {
    const fetchCampaignMessage = async () => {
      if (!selectedTicket?.id) {
        setCampaignMessage(null)
        setConversionNoteValue('')
        setEditingConversion(false)
        return
      }
      try {
        const { data } = await api.get(`/campaign-messages/ticket/${selectedTicket.id}`)
        const msg = data?.[0] || null
        setCampaignMessage(msg)
        setConversionNoteValue(msg?.conversionNote || '')
        setEditingConversion(false)
      } catch (error) {
        // console.log('No campaign message for this ticket')
        void 0;
        setCampaignMessage(null)
        setConversionNoteValue('')
      }
    }
    fetchCampaignMessage()
  }, [selectedTicket?.id])

  // Función para guardar el conversionNote
  const handleSaveConversionNote = async () => {
    if (!campaignMessage?.id) return
    setSavingConversion(true)
    try {
      const { data } = await api.put(`/campaign-messages/${campaignMessage.id}`, {
        conversionNote: conversionNoteValue
      })
      setCampaignMessage(data)
      setEditingConversion(false)

      const purchaseValue = parseFloat(String(conversionNoteValue || '').replace(/[^0-9.]/g, '') || '0')
      if (purchaseValue > 0) {
        setPurchaseConfirmModal({
          open: true,
          campaignMessage: data,
          value: purchaseValue
        })
      }
    } catch (error) {
      console.error('Error saving conversion note:', error)
      toast.error('Error al guardar el valor de conversión')
    } finally {
      setSavingConversion(false)
    }
  }

  const handleSendPurchaseConversionFromTicket = async () => {
    const msg = purchaseConfirmModal.campaignMessage
    const value = purchaseConfirmModal.value
    const contactId = msg?.contactId || selectedTicket?.contact?.id
    const whatsappId = msg?.whatsappId || selectedTicket?.whatsappId

    if (!msg?.id || !contactId || !whatsappId || !value) {
      toast.error('No hay datos suficientes para enviar Purchase a Meta')
      return
    }

    setSendingPurchaseConversion(true)
    try {
      await api.post('/facebook-conversions/send', {
        whatsappId,
        eventName: 'Purchase',
        contactId,
        messageId: msg.messageId,
        ctwaClid: msg.ctwaClid,
        customData: {
          value,
          currency: 'USD'
        }
      })

      toast.success(`Purchase de $${value} USD enviado a Meta`)
      setPurchaseConfirmModal({ open: false, campaignMessage: null, value: 0 })
    } catch (error: any) {
      console.error('Error sending Purchase conversion from ticket:', error)
      toast.error(error?.response?.data?.error || error?.message || 'Error al enviar Purchase a Meta')
    } finally {
      setSendingPurchaseConversion(false)
    }
  }

  // Función para clasificar el estado de entrega (mismo criterio que CampaignsInsights)
  // Esta lógica evita marcar como "COMPLETADA" campañas que aún están activas con presupuesto diario
  const classifyCampaignDelivery = (campaign: any): string => {
    // 1. Si el usuario pausó manualmente la campaña
    if (campaign.status === 'PAUSED') return 'DESACTIVADA'

    // 2. Si effective_status indica que la campaña está completada a nivel de Meta
    if (campaign.effective_status === 'COMPLETED') return 'COMPLETADA'

    // 3. Si CAMPAIGN_PAUSED (pausada por Meta por presupuesto u otros motivos)
    // Solo marcar como completada si además tiene stop_time en el pasado O budget lifetime agotado
    if (campaign.effective_status === 'CAMPAIGN_PAUSED') {
      const hasValidStopTime = campaign.stop_time && campaign.stop_time !== '0000-00-00' && campaign.stop_time !== ''
      const stopTimePassed = hasValidStopTime && new Date(campaign.stop_time) < new Date()
      const budgetExhausted = campaign.budget_remaining !== undefined && Number(campaign.budget_remaining) <= 0
      const hasDailyBudget = campaign.daily_budget !== undefined && Number(campaign.daily_budget) > 0

      // Solo marcar completada si terminó realmente O no hay presupuesto diario activo
      if (stopTimePassed || (budgetExhausted && !hasDailyBudget)) {
        return 'COMPLETADA'
      }
      // De lo contrario está pausada pero podría reactivarse
      return 'PAUSADA'
    }

    // 4. Si el presupuesto lifetime se agotó Y no hay daily budget activo
    const hasLifetimeBudget = campaign.lifetime_budget !== undefined && Number(campaign.lifetime_budget) > 0
    const hasDailyBudget = campaign.daily_budget !== undefined && Number(campaign.daily_budget) > 0
    const budgetRemaining = campaign.budget_remaining !== undefined ? Number(campaign.budget_remaining) : null

    if (hasLifetimeBudget && !hasDailyBudget && budgetRemaining !== null && budgetRemaining <= 0) {
      return 'COMPLETADA'
    }

    // 5. Si la fecha de fin ya pasó (solo para campañas con lifetime budget)
    if (campaign.stop_time && campaign.stop_time !== '0000-00-00' && campaign.stop_time !== '') {
      const stopDate = new Date(campaign.stop_time)
      if (!isNaN(stopDate.getTime()) && stopDate < new Date()) {
        if (!hasDailyBudget || (budgetRemaining !== null && budgetRemaining <= 0)) {
          return 'COMPLETADA'
        }
      }
    }

    // 6. Si no hay ads activos (dato viene del backend)
    if (campaign.activeAds !== undefined && campaign.activeAds === 0) return 'NO_HAY_ANUNCIOS'

    // 7. Si status y effective_status son ACTIVE → la campaña está activa
    if (campaign.status === 'ACTIVE' && campaign.effective_status === 'ACTIVE') return 'ACTIVA'

    // 8. Si solo status es ACTIVE → también activa
    if (campaign.status === 'ACTIVE') return 'ACTIVA'

    // 9. Otros estados de efectivo
    if (campaign.effective_status === 'PAUSED' || campaign.effective_status === 'DELETED' || campaign.effective_status === 'ARCHIVED') {
      return 'DESACTIVADA'
    }

    // Default:假设 activa si tiene presupuesto
    if (hasDailyBudget || hasLifetimeBudget) {
      return 'ACTIVA'
    }

    return 'DESACTIVADA'
  }

  // Función para cargar campañas de Facebook (solo ACTIVAS CON ANUNCIOS)
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true)
    try {
      // Usar /meta-marketing/campaigns en vez de /dashboard
      // porque dashboard usa Promise.all con 4 queries y si alguna falla (insights, ads), todo falla
      // campaigns ya incluye insights + activeAds count
      const { data } = await api.get('/meta-marketing/campaigns', {
        params: { period: 'last_30_days' }
      })

      // Filtrar solo campañas con estado "ACTIVA" (mismo criterio que CampaignsInsights)
      // Esto incluye: status ACTIVE + tiene ads activos (activeAds > 0)
      const allCampaigns = data.campaigns || data || []
      const activeCampaignsWithAds = allCampaigns.filter((campaign: any) => {
        return classifyCampaignDelivery(campaign) === 'ACTIVA'
      })
      setCampaigns(activeCampaignsWithAds)
    } catch (error) {
      console.error('Error fetching campaigns:', error)
      setCampaigns([])
    } finally {
      setLoadingCampaigns(false)
    }
  }

  // Función para cargar orígenes de cliente
  const fetchCustomerOrigins = async () => {
    setLoadingOrigins(true)
    try {
      const { data } = await api.get('/customer-origins', {
        params: { showAll: 'false' } // Solo activos
      })
      setCustomerOrigins(data.records || [])
    } catch (error) {
      console.error('Error fetching customer origins:', error)
      setCustomerOrigins([])
    } finally {
      setLoadingOrigins(false)
    }
  }

  // Referencia para evitar actualizaciones automáticas del Select
  const userInteractedWithOriginSelect = useRef(false)

  // Función para actualizar el origen del cliente en el ticket
  const handleUpdateCustomerOrigin = async (originId: number | string | null) => {
    if (!selectedTicket) return

    // Solo procesar si fue una interacción del usuario, no un cambio automático del Select
    if (!userInteractedWithOriginSelect.current) {
      // console.log('🔍 [CustomerOrigin] Ignorando cambio automático del Select')
      void 0;
      return
    }
    userInteractedWithOriginSelect.current = false // Reset para la próxima vez

    // Convertir string vacío a null para el backend
    const finalOriginId = originId === '' || originId === null ? null : Number(originId)

    // Evitar enviar update si el valor no cambió realmente
    const currentOriginId = (selectedTicket as any)?.customerOriginId || null
    if (finalOriginId === currentOriginId) {
      // console.log('🔍 [CustomerOrigin] Valor no cambió, ignorando')
      void 0;
      return
    }

    // console.log('🔍 [CustomerOrigin] Actualizando:', { from: currentOriginId, to: finalOriginId })
    void 0;

    try {
      await api.put(`/tickets/${selectedTicket.id}`, {
        customerOriginId: finalOriginId
      })
      // Actualizar el ticket localmente
      setSelectedTicket({
        ...selectedTicket,
        customerOriginId: finalOriginId,
        customerOrigin: finalOriginId ? customerOrigins.find(o => o.id === finalOriginId) : null
      } as any)
      // También actualizar en la lista de tickets
      setTickets(prev => prev.map(t =>
        t.id === selectedTicket.id
          ? { ...t, customerOriginId: finalOriginId, customerOrigin: finalOriginId ? customerOrigins.find(o => o.id === finalOriginId) : null } as any
          : t
      ))
    } catch (error) {
      console.error('Error updating customer origin:', error)
    }
  }

  const customerOriginSelector = selectedTicket ? (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        level="body-xs"
        sx={{ color: 'text.tertiary', fontSize: '11px', whiteSpace: 'nowrap' }}
      >
        Origen:
      </Typography>
      <Select
        size="sm"
        placeholder="Origen"
        value={(selectedTicket as any)?.customerOriginId ?? ''}
        onChange={(_, value) => handleUpdateCustomerOrigin(value as number | string | null)}
        sx={{
          minWidth: 132,
          maxWidth: 180,
          '--Select-minHeight': '24px',
          fontSize: '0.7rem',
        }}
        slotProps={{ button: { sx: { fontSize: '0.7rem', py: 0.25 } } }}
        onListboxOpenChange={(isOpen) => {
          if (isOpen) {
            userInteractedWithOriginSelect.current = true
            if (customerOrigins.length === 0) fetchCustomerOrigins()
          }
        }}
      >
        <Option value="">Sin origen</Option>
        {loadingOrigins ? (
          <Option value="" disabled>Cargando...</Option>
        ) : (
          customerOrigins.map(origin => (
            <Option key={origin.id} value={origin.id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: origin.color,
                  }}
                />
                <span>{origin.name}</span>
              </Stack>
            </Option>
          ))
        )}
      </Select>
    </Stack>
  ) : null

  // Cargar anuncios cuando se selecciona una campaña
  useEffect(() => {
    if (!selectedCampaignId) {
      setAds([])
      setSelectedAdId('')
      return
    }
    const fetchAds = async () => {
      setLoadingAds(true)
      try {
        const { data } = await api.get(`/meta-marketing/campaigns/${selectedCampaignId}/ads`)
        setAds(data.ads || data || [])
      } catch (error) {
        console.error('Error fetching ads:', error)
        setAds([])
      } finally {
        setLoadingAds(false)
      }
    }
    fetchAds()
  }, [selectedCampaignId])

  // Función para asignar campaña manualmente al ticket
  const handleAssignCampaign = async () => {
    if (!selectedAdId || !selectedTicket) return

    const selectedAd = ads.find(ad => ad.id === selectedAdId)
    const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId)

    if (!selectedAd) return

    setAssigning(true)
    try {
      const { data } = await api.post('/campaign-messages', {
        ticketId: selectedTicket.id,
        contactId: selectedTicket.contact?.id,
        whatsappId: selectedTicket.whatsappId,
        sourceId: selectedAd.id,
        sourceType: 'MANUAL_ASSIGNMENT',
        headline: selectedAd.name,
        body: selectedAd.creative?.body,
        channel: 'facebook',
        campaignId: selectedCampaignId,
        campaignName: selectedCampaign?.name
      })
      setCampaignMessage(data)
      setConversionNoteValue(data?.conversionNote || '')
      setSelectedCampaignId('')
      setSelectedAdId('')
      setCampaigns([])
      setAds([])
    } catch (error: any) {
      console.error('Error assigning campaign:', error)
      alert(error?.response?.data?.error || 'Error al asignar campaña')
    } finally {
      setAssigning(false)
    }
  }

  const getCampaignMessageCampaignName = (message: any) => {
    const raw = message?.rawData || {}
    return raw.campaignName ||
      raw.campaign_name ||
      raw.campaign?.name ||
      raw.campaign?.campaign_name ||
      raw.ad?.campaign_name ||
      message?.campaignName ||
      ''
  }

  const getCampaignMessageDisplayName = (message: any) => {
    return getCampaignMessageCampaignName(message) || message?.headline || message?.sourceId || 'Anuncio'
  }

  const formatCompactCampaignName = (message: any) => {
    const name = String(getCampaignMessageDisplayName(message))
    return `${name.substring(0, 25)}${name.length > 25 ? '...' : ''}`
  }

  // Limpiar selección de campaña cuando cambia el ticket
  useEffect(() => {
    setSelectedCampaignId('')
    setSelectedAdId('')
    setCampaigns([])
    setAds([])
  }, [selectedTicket?.id])

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!user?.companyId || !user?.id) return

    const socket = socketService.getSocket() ?? socketService.connect(user.companyId, user.id)

    const companyId = user.companyId
    const messageEvent = `company-${companyId}-appMessage`
    const ticketEvent = `company-${companyId}-ticket`
    const contactEvent = `company-${companyId}-contact`
    const messageRecoveryEvent = `company-${companyId}-messageRecovery`

    // console.log(`🔌 Setting up socket listeners for company ${companyId}`)
    void 0;

    const refreshCounts = () => {
      debouncedFetchTicketCounts()
    }

    const refreshVisibleTicketAndCounts = (ticketId: number) => {
      const isVisibleInCurrentList = ticketsRef.current.some(ticket => ticket.id === ticketId)
      const isSelected = selectedTicketRef.current?.id === ticketId

      if (isVisibleInCurrentList || isSelected) {
        void fetchTicketDetails(ticketId)
      }

      refreshCounts()
    }

    // Handler for new/updated messages
    const handleAppMessage = (data: {
      action: string
      message: Message & { ticketId: number; wid?: string }
      ticket?: Ticket
      contact?: Contact
    }) => {
      // console.log('📨 Socket appMessage received:', data.action, data.message?.id, 'wid:', (data.message as any)?.wid)
      void 0;
      // 🔎[ORDEN] LOG TEMPORAL diagnóstico reorden (2026-06-16)
      // console.log('🔎[ORDEN] appMessage', {
      //         action: data.action,
      //         ticketId: data.message?.ticketId,
      //         msgId: data.message?.id,
      //         fromMe: (data.message as any)?.fromMe,
      //         msgCreatedAt: data.message?.createdAt,
      //         socketTicketUpdatedAt: data.ticket?.updatedAt
      //       })
      void 0;

      if (data.action === 'create' || data.action === 'update') {
        const socketTicket = data.ticket

        // Dedup helper: buscar primero por wid (incluye pending_xxx), luego por id
        const findMsgIndex = (messages: Message[]) => {
          const msg = data.message as any
          // 1. Buscar por wid primero (incluye pending_xxx que luego se actualiza)
          let idx = msg.wid ? messages.findIndex(m => (m as any).wid === msg.wid) : -1
          // 2. Si no encuentra, buscar por id
          if (idx < 0 && msg.id) {
            idx = messages.findIndex(m => m.id === msg.id)
          }
          return idx
        }

        const isEditedSocketMessage = (message: Message) => {
          const msg = message as any
          const dataJson = msg.dataJson

          if (msg.isEdited === true) return true
          if (dataJson && typeof dataJson === 'object' && dataJson.lastEdit) return true
          if (typeof dataJson === 'string' && dataJson.includes('"lastEdit"')) return true

          return false
        }

        const shouldReplaceSocketMessage = (existing: Message, incoming: Message) => {
          const existingMsg = existing as any
          const incomingMsg = incoming as any

          if (isEditedSocketMessage(incoming)) return true

          return !existingMsg.ack || (incomingMsg.ack && incomingMsg.ack >= existingMsg.ack)
        }

        // Update messages in the ticket list
        setTickets(prevTickets => {
          const existingTicket = prevTickets.find(ticket => ticket.id === data.message.ticketId)

          if (!existingTicket) {
            if (socketTicket) {
              const incomingTicket = mergeTicketData(null, {
                ...socketTicket,
                contact: socketTicket.contact || data.contact || socketTicket.contact,
                messages: [data.message],
                lastMessage: socketTicket.lastMessage || data.message.body,
                updatedAt: socketTicket.updatedAt || data.message.createdAt
              })

              return upsertTicketInList(prevTickets, incomingTicket)
            }

            void fetchTicketDetails(data.message.ticketId)
            return prevTickets
          }

          return normalizeTickets(prevTickets.map(ticket => {
            if (ticket.id !== data.message.ticketId) return ticket

            const existingMsgIndex = findMsgIndex(ticket.messages)
            let updatedMessages: Message[]

            if (existingMsgIndex >= 0) {
              // Update existing message (keep higher ack)
              updatedMessages = [...ticket.messages]
              if (shouldReplaceSocketMessage(updatedMessages[existingMsgIndex], data.message)) {
                updatedMessages[existingMsgIndex] = data.message
              }
            } else {
              // Add new message. Ordenado por fecha: los mensajes normales llegan en
              // orden y quedan igual, pero los RECUPERADOS son antiguos y deben caer
              // en su lugar cronológico, no al final de la conversación.
              updatedMessages = sortMessagesByCreatedAt([...ticket.messages, data.message])
            }

            const ticketFromSocket = socketTicket && socketTicket.id === ticket.id
              ? mergeTicketData(ticket, {
                  ...socketTicket,
                  contact: socketTicket.contact || data.contact || ticket.contact
                })
              : ticket

            // FECHA SAGRADA: SOLO un mensaje NUEVO (action 'create' no duplicado)
            // mueve la fecha y reordena la lista. Los eventos 'update' (cambios de
            // ACK enviado→entregado→leído, ediciones, o el marcado-como-leído que
            // dispara el click en un ticket) NO deben reordenar. (regresión click 2026-06-16)
            const isNewIncomingMessage = data.action === 'create' && existingMsgIndex < 0
            const nextUpdatedAt = isNewIncomingMessage
              ? (socketTicket?.updatedAt || data.message.createdAt)
              : ticket.updatedAt

            return {
              ...ticketFromSocket,
              messages: updatedMessages,
              lastMessage: isNewIncomingMessage
                ? (socketTicket?.lastMessage || data.message.body)
                : (ticketFromSocket.lastMessage ?? ticket.lastMessage),
              updatedAt: nextUpdatedAt
            }
          }))
        })

        if (socketTicket && !shouldDisplayTicket(socketTicket)) {
          setTickets(prevTickets => removeTicketFromList(prevTickets, socketTicket.id))
        }

        debouncedFetchTicketCounts()

        // Si hay búsqueda activa, NO refrescamos por socket: pisaría los
        // resultados que el operador está viendo. Cuando termine de buscar
        // (cambie/borre el query), el useEffect del filtro recargará todo.
        if (
          !searchActiveRef.current &&
          !socketTicket &&
          !ticketsRef.current.some(ticket => ticket.id === data.message.ticketId)
        ) {
          scheduleTicketListRefresh()
        }

        // Update selected ticket messages
        setSelectedTicket(prevSelected => {
          if (prevSelected && prevSelected.id === data.message.ticketId) {
            const existingMsgIndex = findMsgIndex(prevSelected.messages)
            let updatedMessages: Message[]

            if (existingMsgIndex >= 0) {
              updatedMessages = [...prevSelected.messages]
              if (shouldReplaceSocketMessage(updatedMessages[existingMsgIndex], data.message)) {
                updatedMessages[existingMsgIndex] = data.message
              }
            } else {
              // Mismo fix de orden que en la lista de tickets: mensajes recuperados
              // son antiguos y deben insertarse en su lugar cronológico.
              updatedMessages = sortMessagesByCreatedAt([...prevSelected.messages, data.message])
            }

            const selectedFromSocket = socketTicket && socketTicket.id === prevSelected.id
              ? mergeTicketData(prevSelected, {
                  ...socketTicket,
                  contact: socketTicket.contact || data.contact || prevSelected.contact
                })
              : prevSelected

            // FECHA SAGRADA: igual que en la lista, solo un mensaje nuevo mueve la
            // fecha. Eventos 'update' (ACK/lectura/edición) preservan updatedAt.
            const isNewIncomingMessageSel = data.action === 'create' && existingMsgIndex < 0

            return {
              ...selectedFromSocket,
              messages: updatedMessages,
              lastMessage: isNewIncomingMessageSel
                ? (socketTicket?.lastMessage || data.message.body)
                : (selectedFromSocket.lastMessage ?? prevSelected.lastMessage),
              updatedAt: isNewIncomingMessageSel
                ? (socketTicket?.updatedAt || data.message.createdAt)
                : prevSelected.updatedAt
            }
          }

          return prevSelected
        })
      } else if (data.action === 'delete') {
        const deletedId = data.message?.id
        const ticketId = data.message?.ticketId

        // Remover de tickets
        setTickets(prevTickets =>
          normalizeTickets(prevTickets.map(ticket => ({
            ...ticket,
            messages: ticket.messages.filter((m: Message) => m.id !== deletedId)
          })))
        )

        // Remover del ticket seleccionado
        setSelectedTicket(prevSelected => {
          if (prevSelected && prevSelected.id === ticketId) {
            return {
              ...prevSelected,
              messages: prevSelected.messages.filter((m: Message) => m.id !== deletedId)
            }
          }
          return prevSelected
        })

        // Si estaba en modo edición, salir
        if (editingMessageId === deletedId) {
          setEditingMessageId(null)
          setEditingMessageBody('')
        }

        debouncedFetchTicketCounts()
      }
    }

    const fetchTicketDetails = async (ticketId: number) => {
      const now = Date.now()
      const lastFetchAt = ticketDetailsLastFetchAtRef.current.get(ticketId) || 0

      if (ticketDetailsFetchInFlightRef.current.has(ticketId) || now - lastFetchAt < 1500) {
        return
      }

      ticketDetailsFetchInFlightRef.current.add(ticketId)

      try {
        const { data } = await api.get(`/tickets/${ticketId}`)
        const previousTicket = ticketsRef.current.find(ticket => ticket.id === ticketId)
        const ticketPayload: Partial<Ticket> & { id: number } = {
          id: ticketId,
          ...data
        }

        if (Array.isArray(data?.messages)) {
          ticketPayload.messages = data.messages
          ticketPayload.messagesPageNumber = data.messagesPageNumber || 1
          ticketPayload.messagesHasMore = data.messagesHasMore ?? false
        } else {
          delete (ticketPayload as any).messages
          delete (ticketPayload as any).messagesPageNumber
          delete (ticketPayload as any).messagesHasMore
        }

        const detailedTicket = mergeTicketData(previousTicket, ticketPayload)

        setTickets(prevTickets => upsertTicketInList(prevTickets, detailedTicket))
        setSelectedTicket(prevSelected => (
          prevSelected && prevSelected.id === ticketId
            ? mergeTicketData(prevSelected, detailedTicket)
            : prevSelected
        ))
      } catch (error: any) {
        if (error?.response?.status === 404) {
          setTickets(prevTickets => removeTicketFromList(prevTickets, ticketId))
          setSelectedTicket(prevSelected => (prevSelected?.id === ticketId ? null : prevSelected))
        } else if (!isRequestCanceled(error)) {
          console.error('Error fetching ticket details from socket event:', error)
        }
      } finally {
        ticketDetailsLastFetchAtRef.current.set(ticketId, Date.now())
        ticketDetailsFetchInFlightRef.current.delete(ticketId)
      }
    }

    // Handler for ticket updates
    const handleTicketUpdate = (data: { action: string; ticket?: Ticket; ticketId?: number }) => {
      // console.log('🎫 Socket ticket event received:', data.action, data.ticket?.id || data.ticketId)
      void 0;
      // 🔎[ORDEN] LOG TEMPORAL diagnóstico reorden (2026-06-16)
      // console.log('🔎[ORDEN] ticketEvent', {
      //         action: data.action,
      //         ticketId: data.ticket?.id || data.ticketId,
      //         incomingUpdatedAt: data.ticket?.updatedAt,
      //         traeTicket: !!data.ticket
      //       })
      void 0;

      const incomingTicket = data.ticket
      const incomingTicketId = incomingTicket?.id || data.ticketId

      if (!incomingTicketId) return

      let selectedNeedsHydratedTicket = false

      if (data.action === 'update') {
        if (!incomingTicket) {
          refreshVisibleTicketAndCounts(incomingTicketId)
          return
        }

        const existingTicket = ticketsRef.current.find(ticket => ticket.id === incomingTicketId)
        const mergedIncomingTicket = mergeTicketData(existingTicket, incomingTicket)
        const selectedMergedTicket = selectedTicketRef.current?.id === incomingTicketId
          ? mergeTicketData(selectedTicketRef.current, incomingTicket)
          : null
        const shouldAppearInCurrentList = shouldDisplayTicket(mergedIncomingTicket)
        const needsHydratedTicket = shouldAppearInCurrentList && (
          !mergedIncomingTicket.contact ||
          !mergedIncomingTicket.whatsapp ||
          (mergedIncomingTicket.userId !== undefined && !mergedIncomingTicket.user) ||
          (mergedIncomingTicket.queueId !== undefined && mergedIncomingTicket.queueId !== null && !mergedIncomingTicket.queue)
        )
        selectedNeedsHydratedTicket = !!selectedMergedTicket && (
          !selectedMergedTicket.contact ||
          !selectedMergedTicket.whatsapp ||
          (selectedMergedTicket.userId !== undefined && !selectedMergedTicket.user) ||
          (selectedMergedTicket.queueId !== undefined && selectedMergedTicket.queueId !== null && !selectedMergedTicket.queue)
        )

        if (shouldAppearInCurrentList) {
          setTickets(prevTickets => upsertTicketInList(prevTickets, incomingTicket))
        } else {
          setTickets(prevTickets => removeTicketFromList(prevTickets, incomingTicketId))
        }

        setSelectedTicket(prevSelected => {
          if (prevSelected && prevSelected.id === incomingTicketId) {
            return shouldAppearInCurrentList ? mergeTicketData(prevSelected, incomingTicket) : null
          }
          return prevSelected
        })

        if (needsHydratedTicket) {
          void fetchTicketDetails(incomingTicketId)
        }
      } else if (data.action === 'create') {
        if (incomingTicket) {
          if (shouldDisplayTicket(incomingTicket)) {
            setTickets(prevTickets => upsertTicketInList(prevTickets, incomingTicket))
            if (!incomingTicket.contact || !incomingTicket.whatsapp) {
              void fetchTicketDetails(incomingTicketId)
            }
          } else {
            setTickets(prevTickets => removeTicketFromList(prevTickets, incomingTicketId))
          }
        }
        refreshCounts()
      } else if (data.action === 'delete') {
        setTickets(prevTickets => removeTicketFromList(prevTickets, incomingTicketId))
        setSelectedTicket(prevSelected => (
          prevSelected && prevSelected.id === incomingTicketId ? null : prevSelected
        ))
        refreshCounts()
      }

      if (data.action === 'update' && incomingTicket) {
        const existingTicket = ticketsRef.current.find(ticket => ticket.id === incomingTicketId)
        const statusChanged = existingTicket ? existingTicket.status !== incomingTicket.status : false

        if (statusChanged) {
          refreshCounts()
        } else if (selectedNeedsHydratedTicket) {
          void fetchTicketDetails(incomingTicketId)
        } else {
          refreshCounts()
        }
      }
    }

    // Handler para actualizaciones de contacto en tiempo real (edición desde el
    // ContactDrawer, propia o de otro agente). El backend emite
    // `company-{id}-contact` con action update|delete. Actualizamos el contacto
    // embebido en el ticket seleccionado y en la lista (mismo contactId), sin
    // tocar updatedAt para NO reordenar la lista (FECHA SAGRADA).
    const handleContactUpdate = (data: { action: string; contact?: Contact }) => {
      const incoming = data?.contact
      if (!incoming?.id || data.action === 'delete') return

      setSelectedTicket(prevSelected => {
        if (prevSelected?.contact && prevSelected.contact.id === incoming.id) {
          return { ...prevSelected, contact: { ...prevSelected.contact, ...incoming } }
        }
        return prevSelected
      })

      setTickets(prevTickets =>
        prevTickets.map(t =>
          t.contact?.id === incoming.id
            ? { ...t, contact: { ...t.contact, ...incoming } }
            : t
        )
      )
    }

    const handleSocketConnect = () => {
      // console.log(`🔄 Socket reconnected for company ${companyId}, refreshing ticket list`)
      void 0;
      scheduleTicketListRefresh()
      refreshCounts()
    }

    // Handler para el resultado async de la recuperación de HISTORIAL (la de
    // placeholders/ciphertext llega como appMessage normal). El backend emite
    // esto cuando WhatsApp responde a la solicitud de historial (puede tardar).
    const handleMessageRecovery = (data: {
      action: string
      ticketId: number
      requestId: string
      recoveredCount: number
    }) => {
      if (data.action !== 'history') return

      const isOpenTicket = selectedTicketRef.current?.id === data.ticketId

      if (data.recoveredCount > 0) {
        if (isOpenTicket) {
          toast.success(`Se recuperaron ${data.recoveredCount} mensajes`)
          void fetchTicketDetails(data.ticketId)
        }
      } else {
        if (isOpenTicket) {
          toast.info('WhatsApp no devolvió mensajes nuevos')
        }
      }

      // Los contadores/lista pueden cambiar aunque el ticket no esté abierto.
      refreshCounts()
    }

    // Register listeners
    socket.on(messageEvent, handleAppMessage)
    socket.on(ticketEvent, handleTicketUpdate)
    socket.on(contactEvent, handleContactUpdate)
    socket.on(messageRecoveryEvent, handleMessageRecovery)
    socket.on('connect', handleSocketConnect)

    // console.log(`✅ Socket listeners registered for ${messageEvent}, ${ticketEvent} and ${contactEvent}`)
    void 0;

    // Cleanup on unmount
    return () => {
      // console.log(`🔌 Removing socket listeners for company ${companyId}`)
      void 0;
      socket.off(messageEvent, handleAppMessage)
      socket.off(ticketEvent, handleTicketUpdate)
      socket.off(contactEvent, handleContactUpdate)
      socket.off(messageRecoveryEvent, handleMessageRecovery)
      socket.off('connect', handleSocketConnect)
    }
  }, [
    user?.companyId,
    user?.id,
    debouncedFetchTicketCounts,
    mergeTicketData,
    normalizeTickets,
    removeTicketFromList,
    scheduleTicketListRefresh,
    shouldDisplayTicket,
    upsertTicketInList
  ])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // ─── BUSCAR CONTACTOS PARA NUEVO TICKET ───
  const searchContacts = useCallback(async (term: string) => {
    if (term.length < 2) {
      setNewTicketContacts([])
      return
    }
    try {
      setSearchingNewTicketContacts(true)
      const { data } = await api.get('/contacts', { params: { searchParam: term, pageNumber: 1 } })
      const contacts = (data.contacts || data || []).map((c: any) => ({
        id: c.id,
        name: c.name || c.number,
        number: c.number || '',
      }))
      setNewTicketContacts(contacts)
    } catch (err) {
      console.error('Error buscando contactos:', err)
    } finally {
      setSearchingNewTicketContacts(false)
    }
  }, [])

  const handleContactSearchChange = useCallback((value: string) => {
    setNewTicketContactSearch(value)
    if (contactSearchDebounceRef.current) clearTimeout(contactSearchDebounceRef.current)
    contactSearchDebounceRef.current = setTimeout(() => {
      searchContacts(value)
    }, 400)
  }, [searchContacts])

  const handleCreateNewTicket = async () => {
    if (!newTicketContactId) {
      toast.error('Selecciona un contacto')
      return
    }
    try {
      setNewTicketLoading(true)
      const payload: any = {
        contactId: newTicketContactId,
        status: 'open',
        userId: user?.id,
      }
      if (newTicketQueueId) payload.queueId = Number(newTicketQueueId)
      if (newTicketWhatsappId) payload.whatsappId = Number(newTicketWhatsappId)

      const { data } = await api.post('/tickets', payload)
      toast.success(data?.alreadyOpen ? 'Ticket existente abierto' : 'Ticket creado correctamente')
      setShowNewTicketModal(false)
      setNewTicketContactSearch('')
      setNewTicketContacts([])
      setNewTicketContactId(null)
      setNewTicketQueueId('')
      setNewTicketWhatsappId('')
      await fetchTickets(true)

      // Seleccionar el ticket recién creado
      if (data?.id) {
        const ticketResponse = await api.get(`/tickets/${data.id}`)
        if (ticketResponse.data) {
          setSelectedTicket({
            ...ticketResponse.data,
            messages: ticketResponse.data.messages || [],
            messagesPageNumber: 1,
            messagesHasMore: false
          })
        }
      }
    } catch (error: any) {
      console.error('Error creando ticket:', error)
      toast.error(error.response?.data?.message || 'Error al crear el ticket')
    } finally {
      setNewTicketLoading(false)
    }
  }

  // Auto-selección de ticket cuando se navega desde Contactos
  const pendingContactIdRef = useRef<number | null>(null)
  const pendingTicketIdRef = useRef<number | null>(null)

  useEffect(() => {
    const state = location.state as { contactId?: number; ticketId?: number } | null
    // 2026-07-13: abrir un ticket específico desde el Funnel/Kanban. Antes el Kanban
    // navegaba a /tickets/:uuid (ruta inexistente) → catch-all "*" → Dashboard. Ahora
    // pasa { ticketId } en state y aquí lo abrimos directamente.
    if (state?.ticketId) {
      pendingTicketIdRef.current = state.ticketId
      // Mostrar todos los tickets para encontrar el ticket sin importar status
      setStatusFilter('all')
      setShowAll(true)
      window.history.replaceState({}, '')
    }
    if (state?.contactId) {
      pendingContactIdRef.current = state.contactId
      // Mostrar todos los tickets para encontrar el del contacto sin importar status
      setStatusFilter('all')
      setShowAll(true)
      // Limpiar el state para que no se repita al navegar dentro de tickets
      window.history.replaceState({}, '')
    }
  }, [location.state])

  const fetchTicketsAbortRef = useRef<AbortController | null>(null)

  // Función para cargar tickets con paginación
  // `silent`: si true, NO activa `loading` global (no muestra "Cargando chats..."
  // en pantalla). Usado para refrescos disparados por eventos socket — evita que
  // todos los operadores de la company vean el loading cuando llegan mensajes a
  // tickets que no están en sus listas.
  const fetchTickets = async (reset: boolean = false, explicitPage?: number, silent: boolean = false) => {
    try {
      // Cancel previous request
      if (fetchTicketsAbortRef.current) {
        fetchTicketsAbortRef.current.abort()
      }
      fetchTicketsAbortRef.current = new AbortController()
      const signal = fetchTicketsAbortRef.current.signal

      if (!silent) setLoading(true)
      const currentPage = explicitPage ?? (reset ? 1 : pageNumber)

      if (reset) {
        setPageNumber(1)
      }

      const params: any = {
        showAll: showAll ? 'true' : 'false',
        pageNumber: currentPage,
        limit: 20
      }

      // Cuando hay un término de búsqueda activo se delega al backend (status="search")
      // así se busca en TODA la BD por nombre / número (y mensajes si searchMessages=true).
      // Si no hay búsqueda, se respeta el filtro de status seleccionado por el usuario.
      const trimmedSearch = debouncedSearchTerm.trim()
      const isSearching = trimmedSearch.length > 0

      if (isSearching) {
        params.searchParam = trimmedSearch
        params.status = 'search'
        // FIX: antes era `searchMessages ? 'true' : 'true'` (typo) → SIEMPRE buscaba en la
        // tabla Messages (JOIN + LOWER(unaccent(body)) LIKE → seq scan de millones de filas),
        // aunque el toggle "buscar en mensajes" estuviera apagado. Eso colgaba el buscador al
        // escribir un número. Ahora se respeta el toggle: por defecto la búsqueda va sólo a
        // contacto (nombre/número), que es rápida; sólo si el usuario activa "buscar en
        // mensajes" se hace la búsqueda pesada en el cuerpo de los mensajes.
        params.searchOnMessages = searchMessages ? 'true' : 'false'
      } else if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter
      }

      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      if (whatsappFilter) params.whatsapps = JSON.stringify([Number(whatsappFilter)])
      if (userFilter) params.users = JSON.stringify([Number(userFilter)])
      if (queueFilter) params.queueIds = JSON.stringify([Number(queueFilter)])
      if (tagFilter) params.tags = JSON.stringify([Number(tagFilter)])
      if (!isSearching && searchMessages) params.searchOnMessages = 'true'

      const response = await api.get('/tickets', { params, signal })
      const ticketsData = response.data.tickets || response.data || []
      const hasMoreData = response.data.hasMore ?? false

      setHasMore(hasMoreData)

      if (ticketsData.length === 0) {
        if (reset) {
          setTickets([])
          setSelectedTicket(null)
        }
        return
      }

      // 2026-07-13 PERF: la lista YA NO trae los mensajes de cada ticket. Antes se hacía una
      // llamada GET /messages/:id por CADA ticket de la página (~20 llamadas extra) → lista lenta.
      // Ahora los mensajes se cargan on-demand al ABRIR el ticket (efecto sobre selectedTicket?.id).
      // El preview de la lista usa ticket.lastMessage (ya viene del backend). Se preservan los
      // mensajes ya cargados de un ticket que estuviera abierto para no perder su historial.
      const ticketsWithMessages = ticketsData.map((ticket: Ticket) => {
        const previousTicket = ticketsRef.current.find(currentTicket => currentTicket.id === ticket.id)
        return mergeTicketData(previousTicket, {
          ...ticket,
          messages: previousTicket?.messages || [],
          messagesPageNumber: previousTicket?.messagesPageNumber || 1,
          messagesHasMore: previousTicket?.messagesHasMore ?? false
        })
      })

      if (signal.aborted) return

      // Si es reset, reemplazar; si no, agregar al final
      if (reset) {
        setTickets(normalizeTickets(ticketsWithMessages))
      } else {
        setTickets(prev => normalizeTickets([...prev, ...ticketsWithMessages]))
      }

      // Si venimos del Funnel/Kanban, abrir el ticket exacto por id
      if (pendingTicketIdRef.current) {
        const target = ticketsWithMessages.find(
          (t: Ticket) => t.id === pendingTicketIdRef.current
        )
        if (target) {
          setSelectedTicket(target)
          pendingTicketIdRef.current = null
        } else {
          // No está en la página cargada (con miles de tickets abiertos es lo
          // normal). Se pide directamente al backend en vez de rendirse: antes
          // el ticket simplemente no se abría y parecía que el botón fallaba.
          const idBuscado = pendingTicketIdRef.current
          pendingTicketIdRef.current = null
          api
            .get(`/tickets/${idBuscado}`)
            .then(({ data }) => {
              if (data?.id) setSelectedTicket(normalizeTickets([data])[0])
            })
            .catch(err =>
              console.error('No se pudo abrir el ticket solicitado:', err)
            )
        }
      } else if (pendingContactIdRef.current) {
        // Si venimos de Contactos, buscar y seleccionar el ticket del contacto.
        //
        // NO se hace fallback a ticketsWithMessages[0]: solo se busca en la
        // PÁGINA cargada, y con miles de tickets abiertos el del contacto rara
        // vez está en la primera. Abrir "el primero de la lista" significaba
        // meter al agente en la conversación de otra persona sin avisar — el
        // usuario creía que el botón no funcionaba cuando en realidad le había
        // abierto un chat ajeno.
        const contactTicket = ticketsWithMessages.find(
          (t: Ticket) => t.contactId === pendingContactIdRef.current
        )
        if (contactTicket) {
          setSelectedTicket(contactTicket)
          pendingContactIdRef.current = null
        }
        // Si no está en esta página se conserva la referencia: la siguiente
        // carga (o el scroll) puede encontrarlo. Mejor no abrir nada que abrir
        // el chat equivocado.
      } else if (reset && ticketsWithMessages.length > 0 && !selectedTicket) {
        setSelectedTicket(ticketsWithMessages[0])
      }
    } catch (error: any) {
      if (isRequestCanceled(error)) return
      console.error('❌ Error fetching tickets:', error?.response?.data || error.message || error)
      if (reset) {
        setTickets([])
        setSelectedTicket(null)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // Ref para acceder siempre a la versión más reciente de fetchTickets (evita stale closure en socket handlers)
  useEffect(() => { fetchTicketsRef.current = fetchTickets })

  // Función para cargar más tickets (paginación infinita)
  const loadMoreTickets = async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    const nextPage = pageNumber + 1
    setPageNumber(nextPage)
    // silent=true → NO dispara setLoading(true), así la lista NO se desmonta
    // ni se resetea el scrollTop. Los nuevos tickets se agregan al final
    // (append) y el usuario permanece exactamente donde estaba.
    await fetchTickets(false, nextPage, true)
    setLoadingMore(false)
  }

  // IntersectionObserver para paginación infinita
  useEffect(() => {
    if (!ticketsListRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreTickets()
        }
      },
      { threshold: 0.1 }
    )

    if (ticketsListRef.current) {
      observer.observe(ticketsListRef.current)
    }

    return () => observer.disconnect()
  }, [hasMore, loadingMore, loading, pageNumber])

  const fetchFilterOptions = async () => {
    try {
      const [usersRes, queuesRes, whatsappsRes, tagsRes] = await Promise.all([
        api.get('/users'),
        api.get('/queues'),
        api.get('/whatsapps'),
        // kanban: 0 → solo etiquetas normales. Las etapas del embudo se filtran
        // desde el Kanban, no desde la bandeja.
        api.get('/tags/list', { params: { kanban: 0 } })
      ])

      setUsers(usersRes.data.users || usersRes.data || [])
      setQueues(queuesRes.data.queues || queuesRes.data || [])
      setWhatsapps(whatsappsRes.data.whatsapps || whatsappsRes.data || [])
      setAvailableTags(
        Array.isArray(tagsRes.data) ? tagsRes.data : tagsRes.data?.tags || []
      )
    } catch (error) {
      console.error('Error fetching filter options:', error)
    }
  }

  const handleSelectTicket = (ticket: Ticket) => {
    if (selectedTicket?.id === ticket.id) return

    const nextTicket: Ticket = {
      ...ticket,
      messages: ticket.messages || [],
      messagesPageNumber: ticket.messagesPageNumber || 1,
      messagesHasMore: ticket.messagesHasMore ?? false
    }

    // 2026-07-13: solo seleccionar. Los mensajes se cargan on-demand en el efecto
    // sobre selectedTicket?.id (cubre clicks y auto-selecciones: funnel, contactos, etc.).
    setSelectedTicket(nextTicket)
  }

  // 2026-07-13 PERF: carga on-demand de mensajes del ticket abierto. La lista ya no trae
  // mensajes por ticket, así que aquí cargamos la página 1 al cambiar el ticket
  // seleccionado. Cubre todas las vías de selección.
  //
  // 2026-08-12 FIX — pérdida de historial:
  // Antes había aquí una guarda `if (messages.length > 0) return // ya cargados`.
  // Esa condición daba por hecho que "tener algún mensaje" equivalía a "tener el
  // historial cargado", y eso es falso: cuando llega un mensaje por socket a un
  // chat que aún no se había abierto, se inyecta en `ticket.messages` (línea ~1474)
  // dejando el array con UN solo elemento. Al abrir ese chat la guarda cortaba la
  // carga y la conversación se veía con ese único mensaje, sin historial; además
  // `messagesHasMore: false` impedía recuperarlo con el scroll. Solo un F5 lo
  // arreglaba, porque reconstruía la lista con `messages: []`.
  //
  // Se elimina la guarda: al abrir un chat SIEMPRE se pide la página 1. El coste es
  // una petición por apertura (la misma que ya se hacía la primera vez), y a cambio
  // desaparece toda la clase de fallos por "estado a medias" en el array.
  useEffect(() => {
    const ticketId = selectedTicket?.id
    if (!ticketId) return

    let cancelled = false
    setLoadingMessages(true)
    ;(async () => {
      try {
        const response = await api.get(`/messages/${ticketId}`, { params: { pageNumber: 1 } })
        if (cancelled) return

        const fetchedMessages: Message[] = response.data.messages || []
        const buildPayload = (previousMessages: Message[] | undefined) => ({
          id: ticketId,
          // Fusión en vez de reemplazo: entre que se pidió el historial y que llegó
          // la respuesta pueden haber entrado mensajes por socket, o haber
          // placeholders de un envío en curso. Sustituir el array a secas los hacía
          // desaparecer del chat hasta el siguiente evento.
          messages: mergeMessageLists(previousMessages || [], fetchedMessages),
          messagesPageNumber: 1,
          messagesHasMore: response.data.hasMore ?? false
        })

        setTickets(prevTickets => normalizeTickets(prevTickets.map(currentTicket => (
          currentTicket.id === ticketId
            ? mergeTicketData(currentTicket, buildPayload(currentTicket.messages))
            : currentTicket
        ))))
        setSelectedTicket(prevSelected => (
          prevSelected?.id === ticketId
            ? mergeTicketData(prevSelected, buildPayload(prevSelected.messages))
            : prevSelected
        ))
      } catch (error: any) {
        if (!cancelled && !isRequestCanceled(error)) {
          console.error(`Error loading messages for ticket ${ticketId}:`, error?.response?.data || error.message || error)
        }
      } finally {
        if (!cancelled) setLoadingMessages(false)
      }
    })()

    return () => { cancelled = true; setLoadingMessages(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicket?.id])

  const loadOlderMessages = useCallback(async () => {
    if (!selectedTicket?.id || loadingMoreMessages) return
    if (selectedTicket.messagesHasMore === false) return

    const nextPage = (selectedTicket.messagesPageNumber || 1) + 1
    const container = messagesContainerRef.current
    const previousScrollHeight = container?.scrollHeight || 0
    const previousScrollTop = container?.scrollTop || 0
    const selectedTicketId = selectedTicket.id

    setLoadingMoreMessages(true)
    preserveMessageScrollRef.current = true

    try {
      const response = await api.get(`/messages/${selectedTicketId}`, {
        params: { pageNumber: nextPage }
      })

      const olderMessages: Message[] = response.data.messages || []
      const hasMoreData = response.data.hasMore ?? false

      setSelectedTicket(prevSelected => {
        if (!prevSelected || prevSelected.id !== selectedTicketId) return prevSelected

        const existingIds = new Set(prevSelected.messages.map(message => message.id))
        const dedupedOlderMessages = olderMessages.filter(message => !existingIds.has(message.id))

        return {
          ...prevSelected,
          messages: [...dedupedOlderMessages, ...prevSelected.messages],
          messagesPageNumber: nextPage,
          messagesHasMore: hasMoreData
        }
      })

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            container.scrollTop = newScrollHeight - previousScrollHeight + previousScrollTop
          }
          preserveMessageScrollRef.current = false
        })
      })
    } catch (error) {
      console.error('Error loading older messages:', error)
      preserveMessageScrollRef.current = false
    } finally {
      setLoadingMoreMessages(false)
    }
  }, [selectedTicket, loadingMoreMessages])

  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container || loadingMoreMessages) return

    if (container.scrollTop <= 80) {
      loadOlderMessages()
    }
  }, [loadOlderMessages, loadingMoreMessages])

  const canAttachFilesToSelectedTicket = selectedTicket?.status === 'open' || selectedTicket?.status === 'group'

  const resetChatDragState = useCallback(() => {
    chatDragDepthRef.current = 0
    setIsChatDraggingFiles(false)
  }, [])

  const handleChatDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!dragEventHasFiles(event)) return

    event.preventDefault()
    event.stopPropagation()
    chatDragDepthRef.current += 1
    setIsChatDraggingFiles(true)
  }, [])

  const handleChatDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!dragEventHasFiles(event)) return

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = canAttachFilesToSelectedTicket ? 'copy' : 'none'
  }, [canAttachFilesToSelectedTicket])

  const handleChatDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!dragEventHasFiles(event)) return

    event.preventDefault()
    event.stopPropagation()
    chatDragDepthRef.current = Math.max(0, chatDragDepthRef.current - 1)
    if (chatDragDepthRef.current === 0) {
      setIsChatDraggingFiles(false)
    }
  }, [])

  const handleChatDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!dragEventHasFiles(event)) return

    event.preventDefault()
    event.stopPropagation()
    resetChatDragState()

    const files = Array.from(event.dataTransfer.files || []).filter((file) => file.size > 0)
    if (files.length === 0) return

    if (!canAttachFilesToSelectedTicket) {
      toast.warn('Abre el ticket para adjuntar archivos')
      return
    }

    setDragDropFiles(files)
  }, [canAttachFilesToSelectedTicket, resetChatDragState])

  const handleDroppedFilesHandled = useCallback(() => {
    setDragDropFiles([])
  }, [])

  useEffect(() => {
    setDragDropFiles([])
    resetChatDragState()
  }, [resetChatDragState, selectedTicket?.id])

  const handleCloseTicket = async (ticketId?: number) => {
    const id = ticketId || selectedTicket?.id
    if (!id) return
    try {
      await api.put(`/tickets/${id}`, { status: 'closed' })
      // NO recargamos la lista: el socket `company-X-ticket` con action='update'
      // ya remueve/upsertea el ticket in-place y conserva el scroll del operador.
      fetchTicketCounts() // Solo refresca los badges/contadores, no la lista.
    } catch (error) {
      console.error('Error closing ticket:', error)
    }
  }

  // Confirma la aceptación con un queueId explícito (o el del ticket si ya tenía).
  const confirmAcceptTicket = async (ticket: Ticket, queueId?: number | null) => {
    const newStatus = ticket.isGroup ? 'group' : 'open'
    const payload: { status: string; userId?: number; queueId?: number } = {
      status: newStatus,
    }
    if (user?.id) payload.userId = user.id
    if (queueId) payload.queueId = queueId

    await api.put(`/tickets/${ticket.id}`, payload)

    // Cambiar de tab a la nueva categoría dispara fetch del nuevo filtro
    // a través del useEffect que depende de statusFilter. NO llamamos
    // fetchTickets(true) explícitamente para no romper el scroll del operador.
    setStatusFilter(newStatus)
    fetchTicketCounts()
    // 2026-08-12 FIX: `ticket` viene de la LISTA, cuyo array de mensajes puede
    // estar vacío (la lista ya no carga historial). Asignarlo tal cual borraba la
    // conversación abierta, y como el id del ticket no cambia, el efecto de carga
    // no se volvía a disparar: el chat quedaba en blanco hasta refrescar.
    // Se preserva lo que ya estuviera cargado para este mismo ticket.
    setSelectedTicket(prevSelected => ({
      ...ticket,
      status: newStatus,
      userId: user?.id || ticket.userId,
      queueId: queueId ?? ticket.queueId,
      user: user?.id ? { id: user.id, name: user.name } : ticket.user,
      ...(prevSelected?.id === ticket.id
        ? {
            messages: prevSelected.messages,
            messagesPageNumber: prevSelected.messagesPageNumber,
            messagesHasMore: prevSelected.messagesHasMore
          }
        : {})
    }))
  }

  const handleAcceptTicket = async (ticket: Ticket) => {
    const assignedQueues = currentUserQueues.filter(q => q && typeof q.id === 'number')

    // Si el ticket ya tiene cola, aceptar directo.
    if (ticket.queueId) {
      try {
        await confirmAcceptTicket(ticket)
      } catch (error: any) {
        const apiMsg =
          error?.response?.data?.error || error?.response?.data?.message
        toast.error(apiMsg || 'No se pudo aceptar el ticket')
        console.error('Error accepting ticket:', error)
      }
      return
    }

    // Si el usuario solo tiene una cola asignada, se usa automáticamente.
    if (assignedQueues.length === 1) {
      try {
        await confirmAcceptTicket(ticket, assignedQueues[0].id)
      } catch (error: any) {
        const apiMsg =
          error?.response?.data?.error || error?.response?.data?.message
        toast.error(apiMsg || 'No se pudo aceptar el ticket')
        console.error('Error accepting ticket:', error)
      }
      return
    }

    // Sin cola y con varias opciones: mostrar solo las colas asignadas al usuario.
    setTicketToAccept(ticket)
    setAcceptError(assignedQueues.length === 0 ? 'No tienes colas asignadas para aceptar este ticket' : null)
    setAcceptQueueModalOpen(true)
  }

  const handleAcceptQueueConfirm = async (queueId: number) => {
    if (!ticketToAccept) return
    setAcceptingTicket(true)
    setAcceptError(null)
    try {
      await confirmAcceptTicket(ticketToAccept, queueId)
      setAcceptQueueModalOpen(false)
      setTicketToAccept(null)
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.error || error?.response?.data?.message
      setAcceptError(apiMsg || 'No se pudo aceptar el ticket')
      console.error('Error accepting ticket with queue:', error)
    } finally {
      setAcceptingTicket(false)
    }
  }

  const handleAcceptQueueClose = () => {
    if (acceptingTicket) return
    setAcceptQueueModalOpen(false)
    setTicketToAccept(null)
    setAcceptError(null)
  }

  const handleReopenTicket = async (ticket: Ticket) => {
    try {
      // Reabrir también debe tomar el ticket para el operador actual. Si se
      // envía únicamente el estado, un ticket cerrado sin agente queda abierto
      // con userId=null y desaparece inmediatamente de la vista "Mis tickets".
      const payload: { status: string; userId?: number } = { status: 'open' }
      if (user?.id) payload.userId = user.id

      const { data: reopenedTicket } = await api.put(`/tickets/${ticket.id}`, payload)
      // Cambiar de tab a "open" hace que el useEffect (statusFilter) cargue la
      // lista de abiertos. NO necesitamos fetchTickets(true) — el socket
      // upsertea el ticket reabierto in-place sin perder el scroll.
      setStatusFilter('open')
      fetchTicketCounts() // Solo refresca los badges/contadores.
      // 2026-08-12 FIX: mismo caso que en handleAcceptTicket — ni `ticket` (de la
      // lista) ni la respuesta de la API traen el historial, así que asignarlos
      // directamente vaciaba la conversación al reabrir el ticket.
      setSelectedTicket(prevSelected => ({
        ...ticket,
        ...reopenedTicket,
        status: 'open',
        userId: user?.id || reopenedTicket?.userId || ticket.userId,
        user: reopenedTicket?.user || (user?.id ? { id: user.id, name: user.name } : ticket.user),
        ...(prevSelected?.id === ticket.id
          ? {
              messages: prevSelected.messages,
              messagesPageNumber: prevSelected.messagesPageNumber,
              messagesHasMore: prevSelected.messagesHasMore
            }
          : {})
      }))
    } catch (error: any) {
      const apiMsg =
        error?.response?.data?.error || error?.response?.data?.message
      toast.error(apiMsg || 'No se pudo reabrir el ticket')
      console.error('Error reopening ticket:', error)
    }
  }

  // formatTime, formatDate, formatTicketDate, formatDateSeparator, shouldShowDateSeparator
  // ahora vienen del hook useMessageFormatting()

  // El backend ya devuelve los tickets filtrados (status='search' + searchParam)
  // cuando hay búsqueda activa. Mantenemos `filteredTickets` como la lista
  // recibida para no ocultar coincidencias de mensajes que no aparecen en
  // contact.name/number/lastMessage.
  //
  // Filtro CLIENT-SIDE por CANAL ACTIVO (chips Todos/WhatsApp/Meta/FB/IG/Telegram).
  // Coexistencia: 'whatsapp' agrupa Baileys/whatsapp (mismo transporte WhatsApp).
  // NO crea conversaciones separadas: solo acota la vista de la bandeja.
  const filteredTickets = useMemo(() => {
    if (!channelFilter || channelFilter === 'all') return tickets
    return tickets.filter((ticket) => {
      const tChannel = ((ticket as any).channel || 'whatsapp').toLowerCase()
      if (channelFilter === 'whatsapp') return ['whatsapp', 'baileys'].includes(tChannel)
      return tChannel === channelFilter
    })
  }, [tickets, channelFilter])

  const queueMap = useMemo(
    () => new Map(queues.map(queue => [queue.id, queue])),
    [queues]
  )

  const userMap = useMemo(
    () => new Map(users.map(item => [item.id, item])),
    [users]
  )

  const currentUserQueues = useMemo(() => {
    const currentUser = users.find(item => item.id === user?.id)
    const assignedQueues = currentUser?.queues || []

    if (assignedQueues.length === 0) {
      return []
    }

    const assignedQueueIds = new Set(assignedQueues.map(queue => queue.id))
    const hydratedQueues = queues.filter(queue => assignedQueueIds.has(queue.id))
    return hydratedQueues.length > 0 ? hydratedQueues : assignedQueues
  }, [queues, user?.id, users])

  const whatsappMap = useMemo(
    () => new Map(whatsapps.map(item => [item.id, item])),
    [whatsapps]
  )

  const getResolvedQueue = useCallback((ticket: Ticket) => {
    if (ticket.queue?.name) return ticket.queue
    if (!ticket.queueId) return null
    const fallbackQueue = queueMap.get(ticket.queueId)
    return fallbackQueue ? { ...fallbackQueue, color: fallbackQueue.color || '#64748B' } : null
  }, [queueMap])

  const getResolvedUser = useCallback((ticket: Ticket) => {
    if (ticket.user?.name) return ticket.user
    if (!ticket.userId) return null
    return userMap.get(ticket.userId) || null
  }, [userMap])

  const getResolvedWhatsapp = useCallback((ticket: Ticket) => {
    if (ticket.whatsapp?.name) return ticket.whatsapp
    if (!ticket.whatsappId) return null
    return whatsappMap.get(ticket.whatsappId) || null
  }, [whatsappMap])

  /**
   * Etiquetas visibles de un ticket.
   *
   * Antes esto era un fallback EXCLUYENTE: si el ticket tenía etiquetas propias,
   * las del contacto dejaban de mostrarse. Con el auto-etiquetado eso provocaba
   * una regresión visible en 664 tickets abiertos que hoy muestran las etiquetas
   * de su contacto: en cuanto el motor escribiera una etiqueta en el ticket,
   * las del contacto desaparecían de la bandeja (sin haberse borrado nada).
   *
   * Ahora es un MERGE deduplicado por id, dando prioridad a las del ticket.
   */
  const getResolvedTags = useCallback((ticket: Ticket) => {
    const delTicket = Array.isArray(ticket.tags) ? ticket.tags : []
    const delContacto = Array.isArray(ticket.contact?.tags) ? ticket.contact.tags : []

    if (delContacto.length === 0) return delTicket
    if (delTicket.length === 0) return delContacto

    const vistos = new Set(delTicket.map((t) => t.id))
    return [...delTicket, ...delContacto.filter((t) => !vistos.has(t.id))]
  }, [])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <OpenIcon sx={{ fontSize: 16, color: 'success.main' }} />
      case 'pending':
        return <PendingIcon sx={{ fontSize: 16, color: 'warning.main' }} />
      case 'closed':
        return <ResolvedIcon sx={{ fontSize: 16, color: 'neutral.main' }} />
      default:
        return null
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open':
        return 'Abierto'
      case 'pending':
        return 'Pendiente'
      case 'closed':
        return 'Cerrado'
      default:
        return status
    }
  }

  // ══════════════════════════════════════════
  // HANDLERS DE EDICIÓN DE MENSAJES
  // ══════════════════════════════════════════
  const handleStartEditMessage = (messageId: number, currentBody: string) => {
    setEditingMessageId(messageId)
    setEditingMessageBody(currentBody)
    setMessageActionMenu(null)
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null)
    setEditingMessageBody('')
  }

  const handleSaveEditMessage = async () => {
    if (!editingMessageId || !editingMessageBody.trim()) return
    setSavingEdit(true)
    try {
      await api.post(`/messages/edit/${editingMessageId}`, {
        body: editingMessageBody.trim()
      })
      setEditingMessageId(null)
      setEditingMessageBody('')
    } catch (error: any) {
      console.error('Error editando mensaje:', error)
      toast.error(error?.response?.data?.error || 'Error al editar mensaje')
    } finally {
      setSavingEdit(false)
    }
  }

  // ══════════════════════════════════════════
  // HANDLERS DE SELECCIÓN MÚLTIPLE
  // ══════════════════════════════════════════
  const isMessageDeletedForActions = (message: Message) => (
    Boolean(message.isDeleted || message.messageStatus === 'deleted')
  )

  const canSelectMessageForMode = (
    message: Message,
    mode: 'forward' | 'delete' | null = selectionMode
  ) => {
    if (!mode || isMessageDeletedForActions(message)) return false
    if (mode === 'forward') return !message.isPrivate
    return Boolean(message.fromMe)
  }

  const handleEnterSelectionMode = (
    mode: 'forward' | 'delete',
    message: Message
  ) => {
    if (!canSelectMessageForMode(message, mode)) {
      if (mode === 'delete' && !message.fromMe) {
        toast.warning('Solo puedes eliminar tus propios mensajes')
      }
      return
    }
    if (mode === 'delete' && !message.fromMe) {
      toast.warning('Solo puedes eliminar tus propios mensajes')
      return
    }
    setSelectionMode(mode)
    setSelectedMessageIds(new Set([message.id]))
    setMessageActionMenu(null)
    setEditingMessageId(null)
  }

  const handleToggleMessageSelection = (message: Message) => {
    if (!canSelectMessageForMode(message)) {
      if (selectionMode === 'delete' && !message.fromMe) {
        toast.warning('Solo puedes eliminar tus propios mensajes')
      }
      return
    }

    setSelectedMessageIds(prev => {
      const next = new Set(prev)
      if (next.has(message.id)) {
        next.delete(message.id)
      } else {
        next.add(message.id)
      }
      if (next.size === 0) {
        setSelectionMode(null)
      }
      return next
    })
  }

  const handleExitSelectionMode = () => {
    setSelectionMode(null)
    setSelectedMessageIds(new Set())
    setMessageActionMenu(null)
  }

  const handleDeleteMessages = async () => {
    if (selectedMessageIds.size === 0) return
    const count = selectedMessageIds.size
    const confirmed = window.confirm(
      `¿Eliminar ${count} mensaje${count > 1 ? 's' : ''}? Esta acción no se puede deshacer.`
    )
    if (!confirmed) return
    setActionLoading(true)
    try {
      await Promise.all(
        Array.from(selectedMessageIds).map(messageId =>
          api.delete(`/messages/${messageId}`)
        )
      )
      toast.success(`${count} mensaje${count > 1 ? 's' : ''} eliminad${count > 1 ? 'os' : 'o'}`)
      handleExitSelectionMode()
    } catch (error: any) {
      console.error('Error eliminando mensajes:', error)
      toast.error(error?.response?.data?.error || 'Error al eliminar mensajes')
    } finally {
      setActionLoading(false)
    }
  }

  // Antes se hacia `Promise.all` sobre mensajes x destinos: todo a la vez. Eso
  // creaba tickets duplicados, desordenaba los mensajes y, al rechazar al primer
  // fallo, cancelaba los destinos restantes sin decir cuales habian salido.
  // Ahora: cada destino recibe SUS mensajes en orden y uno tras otro; los
  // destinos van en paralelo con un maximo de 4 y nada aborta al resto.
  const handleForwardMessages = async (
    contactIds: number[],
    destinations?: ForwardDestination[]
  ) => {
    if (selectedMessageIds.size === 0 || contactIds.length === 0) return
    setForwardLoading(true)
    try {
      const selectedMessages = (selectedTicket?.messages || [])
        .filter(
          message => selectedMessageIds.has(message.id) && canSelectMessageForMode(message, 'forward')
        )
        .slice()
        // Orden cronologico: el destino debe recibirlos como se escribieron.
        .sort(
          (a, b) =>
            new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
        )

      if (selectedMessages.length === 0) {
        toast.warning('No hay mensajes válidos para reenviar')
        return
      }

      const destinos: ForwardDestination[] =
        destinations && destinations.length
          ? destinations
          : contactIds.map(id => ({ id, name: `#${id}`, number: '', isGroup: false }))

      const tareas = destinos.map(destino => async () => {
        for (const message of selectedMessages) {
          await api.post('/message/forward', {
            messageId: message.id,
            contactId: destino.id
          })
        }
        return destino
      })

      const resultados = await settleWithConcurrency(tareas, 4)

      const fallidos = resultados
        .map((r, i) => ({ r, destino: destinos[i] }))
        .filter(x => !x.r.ok)
      const correctos = destinos.length - fallidos.length

      const etiqueta = (lista: ForwardDestination[]) => {
        const c = lista.filter(d => !d.isGroup).length
        const g = lista.filter(d => d.isGroup).length
        const partes: string[] = []
        if (c) partes.push(`${c} contacto${c > 1 ? 's' : ''}`)
        if (g) partes.push(`${g} grupo${g > 1 ? 's' : ''}`)
        return partes.join(' y ')
      }

      const nMsg = selectedMessages.length
      const textoMsg = `${nMsg} mensaje${nMsg > 1 ? 's' : ''}`

      if (fallidos.length === 0) {
        toast.success(`${textoMsg} reenviado${nMsg > 1 ? 's' : ''} a ${etiqueta(destinos)}`)
        setShowForwardModal(false)
        handleExitSelectionMode()
      } else if (correctos > 0) {
        const detalle = fallidos
          .map(x => `${x.destino.name} (${motivoDeError((x.r as any).error)})`)
          .join('; ')
        toast.warning(
          `Enviados ${correctos} de ${destinos.length}. Fallaron: ${detalle}`
        )
        setShowForwardModal(false)
        handleExitSelectionMode()
      } else {
        // No salio ninguno: el selector se queda abierto para corregir.
        const detalle = fallidos
          .map(x => `${x.destino.name} (${motivoDeError((x.r as any).error)})`)
          .join('; ')
        toast.error(`No se pudo reenviar. ${detalle}`)
      }
    } catch (error: any) {
      console.error('Error reenviando mensajes:', error)
      toast.error(motivoDeError(error) || 'Error al reenviar mensajes')
    } finally {
      setForwardLoading(false)
    }
  }

  // ══════════════════════════════════════════
  // HANDLERS DE MENÚ DE ACCIONES
  // ══════════════════════════════════════════
  const handleOpenMessageMenu = (
    event: React.MouseEvent<HTMLElement>,
    messageId: number,
    fromMe: boolean
  ) => {
    event.stopPropagation()
    setMessageActionMenu({
      messageId,
      anchorEl: event.currentTarget,
      fromMe
    })
  }

  const handleCloseMessageMenu = () => {
    setMessageActionMenu(null)
  }

  // Determina si un mensaje tiene un archivo descargable (imagen, video, audio, documento)
  const getDownloadableMedia = (
    msg?: { mediaUrl?: string; mediaType?: string; body?: string; isUploading?: boolean }
  ): { url: string; name: string } | null => {
    if (!msg || !msg.mediaUrl || msg.isUploading) return null
    const type = (msg.mediaType || '').toLowerCase()
    // Excluir tipos que no son archivos descargables
    if (['ciphertext', 'contact', 'vcard', 'location', 'admetapreview'].includes(type)) {
      return null
    }
    const rawName = msg.mediaUrl.split('/').pop() || 'descarga'
    // Nombre legible: preferir el body si parece un nombre de archivo, si no el de la URL
    const name = decodeURIComponent(rawName)
    const base = `${BACKEND_URL}/public/company${user?.companyId}/${msg.mediaUrl}`
    const url = `${base}${base.includes('?') ? '&' : '?'}download=${encodeURIComponent(name)}`
    return { url, name }
  }

  // ─── Reflejo en tiempo real de los cambios del ContactDrawer ───
  // El drawer notifica sus mutaciones (contacto / ticket) para actualizar al
  // instante el header de la conversación y la lista, sin esperar el socket.
  // Tipamos con `unknown` + cast para puentear los tipos del drawer sin `any`.
  const handleContactPatchedFromDrawer = useCallback((patch: unknown) => {
    const p = patch as Partial<Contact>
    const contactId = selectedTicketRef.current?.contact?.id
    setSelectedTicket(prev =>
      prev && prev.contact ? { ...prev, contact: { ...prev.contact, ...p } } : prev
    )
    if (contactId) {
      setTickets(prev =>
        prev.map(t =>
          t.contact?.id === contactId ? { ...t, contact: { ...t.contact, ...p } } : t
        )
      )
    }
  }, [])

  const handleTicketPatchedFromDrawer = useCallback((patch: unknown) => {
    const p = patch as Partial<Ticket>
    const id = selectedTicketRef.current?.id
    if (!id) return
    // FECHA SAGRADA: no incluimos updatedAt, por lo que NO se reordena la lista.
    setSelectedTicket(prev => (prev && prev.id === id ? { ...prev, ...p } : prev))
    setTickets(prev => prev.map(t => (t.id === id ? { ...t, ...p } : t)))
  }, [])

  return (
    <CssVarsProvider theme={facebookTheme}>
      {/* Page background - fondo muted para contraste con la card */}
      <Box
        sx={{
          height: '100%',
          bgcolor: isDark ? '#111213' : '#F4F5F7',
          m: { xs: -2, sm: -3 },
          p: '1px',
        }}
      >
      {/* Card container unificado estilo Shadcn */}
      <Box
        sx={{
          display: 'flex',
          // 100dvh descuenta la barra del navegador movil; sin esto el campo de
          // escritura queda debajo del borde visible. 100vh queda de respaldo.
          height: { xs: 'calc(100dvh - 66px)', md: 'calc(100vh - 66px)' },
          bgcolor: 'background.body',
          borderRadius: '4px',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          boxShadow: isDark ? facebookDesignTokens.card.shadowDark : facebookDesignTokens.card.shadow,
          overflow: 'hidden',
        }}
      >
        {/* Sidebar - Tickets List */}
        <Box
          sx={{
            width: ticketSidebarWidth,
            minWidth: ticketSidebarWidth,
            maxWidth: ticketSidebarWidth,
            // oculta la lista y deja el ancho completo a la conversacion
            display: chatAmpliado && selectedTicket ? 'none' : 'flex',
            borderRight: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            flexDirection: 'column',
            bgcolor: 'background.surface',
            overflow: 'hidden',
          }}
        >
        {/* Sidebar Header */}
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="h4" sx={{ fontWeight: 700 }}>Chats</Typography>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Tooltip
                  title={showAll ? 'Mostrar solo mis tickets' : 'Mostrar todos los tickets'}
                  placement="top"
                >
                  <IconButton
                    size="sm"
                    variant="plain"
                    color={showAll ? 'primary' : 'neutral'}
                    onClick={() => {
                      setShowAll(!showAll)
                      fetchTicketCounts()
                    }}
                    sx={{
                      borderRadius: '50%',
                      color: showAll ? '#5BC2D2' : 'text.secondary',
                      '&:hover': { bgcolor: 'background.level2', color: '#5BC2D2' },
                    }}
                  >
                    {showAll ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Nuevo ticket" placement="top">
                  <IconButton
                    size="sm"
                    variant="solid"
                    color="primary"
                    onClick={() => setShowNewTicketModal(true)}
                    sx={{
                      borderRadius: '50%',
                      bgcolor: '#5BC2D2',
                      '&:hover': { bgcolor: '#4AA8B8' },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Actualizar" placement="top">
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => {
                      fetchTickets(true)
                      fetchTicketCounts()
                    }}
                    sx={{
                      borderRadius: '50%',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'background.level2', color: '#5BC2D2' },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}>
                  <IconButton
                    size="sm"
                    variant={showFilters ? 'solid' : 'plain'}
                    color={showFilters ? 'primary' : 'neutral'}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ borderRadius: '50%' }}
                  >
                    {showFilters ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <FilterIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
            <Input
              placeholder="Buscar tickets y mensajes..."
              startDecorator={<SearchIcon />}
              value={searchTerm}
              onChange={handleSearchChange}
              size="sm"
            />

            {/* Filtros colapsables */}
            {showFilters && (
              <>
                {/* Filtro de rango de fechas - un solo selector (ahorra espacio) */}
                <FormControl size="sm">
                  <FormLabel>Rango de fechas</FormLabel>
                  <DateRangePicker
                    since={startDate}
                    until={endDate}
                    presetLabel=""
                    months={1}
                    showPresets={false}
                    align="left"
                    allowClear
                    showRangeInTrigger
                    fullWidth
                    placeholder="Todas las fechas"
                    onApply={(s, u) => {
                      setStartDate(s)
                      setEndDate(u)
                    }}
                  />
                </FormControl>

                {/* Connection Filter */}
                <FormControl size="sm">
                  <FormLabel>Conexión (WhatsApp)</FormLabel>
                  <Select
                    value={whatsappFilter}
                    onChange={(_, value) => setWhatsappFilter(value as string)}
                    size="sm"
                  >
                    <Option value="">Todas las conexiones</Option>
                    {whatsapps.map((whatsapp) => (
                      <Option key={whatsapp.id} value={whatsapp.id.toString()}>
                        {whatsapp.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {/* Users Filter */}
                <FormControl size="sm">
                  <FormLabel>Usuario</FormLabel>
                  <Select
                    value={userFilter}
                    onChange={(_, value) => setUserFilter(value as string)}
                    size="sm"
                  >
                    <Option value="">Todos los usuarios</Option>
                    {users.map((user) => (
                      <Option key={user.id} value={user.id.toString()}>
                        {user.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {/* Queues Filter */}
                <FormControl size="sm">
                  <FormLabel>Cola</FormLabel>
                  <Select
                    value={queueFilter}
                    onChange={(_, value) => setQueueFilter(value as string)}
                    size="sm"
                  >
                    <Option value="">Todas las colas</Option>
                    {queues.map((queue) => (
                      <Option key={queue.id} value={queue.id.toString()}>
                        {queue.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {/* Tags Filter */}
                <FormControl size="sm">
                  <FormLabel>Etiqueta</FormLabel>
                  <Select
                    value={tagFilter}
                    onChange={(_, value) => setTagFilter(value as string)}
                    size="sm"
                  >
                    <Option value="">Todas las etiquetas</Option>
                    {availableTags.map((tag) => (
                      <Option key={tag.id} value={tag.id.toString()}>
                        <Box
                          component="span"
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: tag.color || '#94a3b8',
                            display: 'inline-block',
                            mr: 1,
                            flexShrink: 0,
                          }}
                        />
                        {tag.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                {/* Search Messages Toggle */}
                <FormControl orientation="horizontal" sx={{ alignItems: 'center' }}>
                  <FormLabel>Buscar en mensajes</FormLabel>
                  <Switch
                    checked={searchMessages}
                    onChange={(e) => setSearchMessages(e.target.checked)}
                    size="sm"
                  />
                </FormControl>
              </>
            )}

          </Stack>
        </Box>

        {/* Tabs con contadores - Diseño moderno */}
        <Box sx={{
          p: 1,
          bgcolor: 'background.level1',
        }}>
          <Tabs
            value={statusFilter}
            onChange={(_, value) => {
              if (value !== null) {
                setStatusFilter(value as string)
              }
            }}
            sx={{
              bgcolor: 'transparent',
              '--Tabs-gap': '8px',
            }}
          >
            <TabList
              disableUnderline
              sx={{
                display: 'flex',
                gap: 0.5,
                p: 0.375,
                borderRadius: 'md',
                bgcolor: 'background.surface',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              {/* Tab Abiertos - Verde */}
              <Tooltip
                title={
                  <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#22c55e', borderRadius: '8px' }}>
                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem' }}>Abiertos</Typography>
                  </Box>
                }
                placement="top"
                variant="plain"
              >
                <Tab
                  value="open"
                  disableIndicator
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 0.75,
                    borderRadius: 'md',
                    fontWeight: 600,
                    minHeight: 38,
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(34, 197, 94, 0.1)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                    <MessageIcon sx={{ fontSize: 22 }} />
                    {openCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: statusFilter === 'open' ? 'rgba(255,255,255,0.3)' : '#22c55e',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          border: '2px solid white',
                        }}
                      >
                        {openCount}
                      </Box>
                    )}
                  </Box>
                </Tab>
              </Tooltip>

              {/* Tab Pendientes - Naranja */}
              <Tooltip
                title={
                  <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#f97316', borderRadius: '8px' }}>
                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem' }}>Pendientes</Typography>
                  </Box>
                }
                placement="top"
                variant="plain"
              >
                <Tab
                  value="pending"
                  disableIndicator
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 0.75,
                    borderRadius: 'md',
                    fontWeight: 600,
                    minHeight: 38,
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(249, 115, 22, 0.1)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                    <AccessTimeIcon sx={{ fontSize: 22 }} />
                    {pendingCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: statusFilter === 'pending' ? 'rgba(255,255,255,0.3)' : '#f97316',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          border: '2px solid white',
                        }}
                      >
                        {pendingCount}
                      </Box>
                    )}
                  </Box>
                </Tab>
              </Tooltip>

              {/* Tab Grupos - Teal */}
              <Tooltip
                title={
                  <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#5BC2D2', borderRadius: '8px' }}>
                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem' }}>Grupos</Typography>
                  </Box>
                }
                placement="top"
                variant="plain"
              >
                <Tab
                  value="group"
                  disableIndicator
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 0.75,
                    borderRadius: 'md',
                    fontWeight: 600,
                    minHeight: 38,
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(91, 194, 210, 0.1)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'linear-gradient(135deg, #5BC2D2 0%, #4BA8B6 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(91, 194, 210, 0.4)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                    <GroupIcon sx={{ fontSize: 22 }} />
                    {groupCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: statusFilter === 'group' ? 'rgba(255,255,255,0.3)' : '#5BC2D2',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          border: '2px solid white',
                        }}
                      >
                        {groupCount}
                      </Box>
                    )}
                  </Box>
                </Tab>
              </Tooltip>

              {/* Tab Cerrados - Gris */}
              <Tooltip
                title={
                  <Box sx={{ px: 1.5, py: 0.5, bgcolor: '#6b7280', borderRadius: '8px' }}>
                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.875rem' }}>Cerrados</Typography>
                  </Box>
                }
                placement="top"
                variant="plain"
              >
                <Tab
                  value="closed"
                  disableIndicator
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 0.75,
                    borderRadius: 'md',
                    fontWeight: 600,
                    minHeight: 38,
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(107, 114, 128, 0.1)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                      color: 'white',
                      boxShadow: '0 4px 12px rgba(107, 114, 128, 0.4)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                    <CheckBoxIcon sx={{ fontSize: 22 }} />
                    {closedCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          bgcolor: statusFilter === 'closed' ? 'rgba(255,255,255,0.3)' : '#6b7280',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          minWidth: 22,
                          height: 22,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 0.5,
                          border: '2px solid white',
                        }}
                      >
                        {closedCount}
                      </Box>
                    )}
                  </Box>
                </Tab>
              </Tooltip>
            </TabList>
          </Tabs>

          {/* Cierre masivo: solo admin, y solo con la pestaña Abiertos o Pendientes
              activa, porque el backend unicamente admite esos dos estados. */}
          {user?.profile === 'admin' && (statusFilter === 'open' || statusFilter === 'pending') && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size="sm"
                variant="soft"
                color="danger"
                loading={cerrandoTodos}
                disabled={(statusFilter === 'open' ? openCount : pendingCount) === 0}
                onClick={() => setConfirmarCierreMasivo(true)}
                startDecorator={<CheckBoxIcon sx={{ fontSize: 16 }} />}
              >
                Cerrar todos ({statusFilter === 'open' ? openCount : pendingCount})
              </Button>
            </Box>
          )}

          {/* Filtros por CANAL ACTIVO (no crean conversaciones separadas) */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              mt: 1,
              overflowX: 'auto',
              pb: 0.5,
              ...thinScrollbarSx,
            }}
          >
            {[
              { key: 'all', label: 'Todos', icon: null, color: '#3b82f6' },
              { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon sx={{ fontSize: 15 }} />, color: '#25D366' },
              { key: 'meta', label: 'Meta', icon: <WhatsAppIcon sx={{ fontSize: 15 }} />, color: '#0866FF' },
              { key: 'facebook', label: 'Facebook', icon: <FacebookIcon sx={{ fontSize: 15 }} />, color: '#4267B2' },
              { key: 'instagram', label: 'Instagram', icon: <InstagramIcon sx={{ fontSize: 15 }} />, color: '#E1306C' },
              { key: 'telegram', label: 'Telegram', icon: <TelegramIcon sx={{ fontSize: 15 }} />, color: '#0088cc' },
            ].map((ch) => {
              const active = channelFilter === ch.key
              return (
                <Chip
                  key={ch.key}
                  size="sm"
                  variant={active ? 'solid' : 'soft'}
                  onClick={() => setChannelFilter(ch.key)}
                  startDecorator={ch.icon || undefined}
                  sx={{
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontWeight: 600,
                    ...(active
                      ? { bgcolor: ch.color, color: 'white', '&:hover': { bgcolor: ch.color } }
                      : {}),
                  }}
                >
                  {ch.label}
                </Chip>
              )
            })}
          </Box>
        </Box>

        {/* Tickets List */}
        <Sheet sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, minHeight: 0, ...thinScrollbarSx }}>
          <List sx={{ py: 0 }}>
            {loading ? (
              <ListItem>
                <Typography level="body-sm" sx={{ p: 2 }}>
                  Cargando chats...
                </Typography>
              </ListItem>
            ) : filteredTickets.length === 0 ? (
              <ListItem>
                <Typography level="body-sm" sx={{ p: 2, color: 'text.tertiary' }}>
                  No se encontraron chats
                </Typography>
              </ListItem>
            ) : (
              filteredTickets.map((ticket) => {
                // Función para obtener icono de canal
                const getChannelIcon = (channel?: string, size: number = 13) => {
                  switch (channel) {
                    case 'whatsapp':
                      return <WhatsAppIcon sx={{ fontSize: size, color: '#25D366' }} />
                    case 'telegram':
                      return <TelegramIcon sx={{ fontSize: size, color: '#0088cc' }} />
                    case 'facebook':
                      return <FacebookIcon sx={{ fontSize: size, color: '#4267B2' }} />
                    case 'instagram':
                      return <InstagramIcon sx={{ fontSize: size, color: '#E1306C' }} />
                    default:
                      return <WhatsAppIcon sx={{ fontSize: size, color: '#25D366' }} />
                  }
                }

                // Función para obtener color del canal
                const getChannelColor = (channel?: string) => {
                  switch (channel) {
                    case 'whatsapp': return '#25D366'
                    case 'telegram': return '#0088cc'
                    case 'facebook': return '#4267B2'
                    case 'instagram': return '#E1306C'
                    default: return '#25D366'
                  }
                }

                const resolvedQueue = getResolvedQueue(ticket)
                const resolvedUser = getResolvedUser(ticket)
                const resolvedWhatsapp = getResolvedWhatsapp(ticket)
                const resolvedTags = getResolvedTags(ticket)

                return (
                  <ListItem key={ticket.id} sx={{ p: 0, mx: 0.5, my: 0.15 }}>
                    <ListItemButton
                      selected={selectedTicket?.id === ticket.id}
                              onClick={() => handleSelectTicket(ticket)}
                      sx={{
                        py: 0.65,
                        px: 1.5,
                        gap: 1,
                        borderRadius: '8px',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': {
                          bgcolor: isDark ? '#3A3B3C' : '#F0F2F5',
                          '& .ticket-actions': { opacity: 1 },
                        },
                        '&.Mui-selected': {
                          bgcolor: isDark ? 'rgba(111,212,228,0.15)' : 'rgba(91,194,210,0.12)',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(111,212,228,0.2)' : 'rgba(91,194,210,0.18)',
                          },
                        },
                      }}
                    >
                      <ListItemDecorator>
                        <Box sx={{ position: 'relative' }}>
                          <Badge
                            badgeContent={ticket.unreadMessages}
                            max={99}
                            size="sm"
                            invisible={ticket.unreadMessages === 0}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            sx={{
                              '& .MuiBadge-badge': {
                                transform: 'translate(15%, -15%)',
                                zIndex: 3,
                                minWidth: 18,
                                height: 18,
                                fontSize: '10px',
                                fontWeight: 700,
                                bgcolor: getChannelColor(ticket.channel),
                                color: 'white',
                                border: '2px solid ' + (isDark ? '#242526' : '#FFFFFF'),
                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                              },
                            }}
                          >
                            {ticket.isGroup ? (
                              <Avatar size="lg" sx={{ width: 36, height: 36 }}>
                                <GroupIcon />
                              </Avatar>
                            ) : (
                              <Avatar size="lg" src={ticket.contact?.urlPicture || ticket.contact?.profilePicUrl} sx={{ width: 36, height: 36 }}>
                                {contactAvatarInitial(ticket.contact)}
                              </Avatar>
                            )}
                          </Badge>
                          {/* Channel icon overlay en el avatar */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -2,
                              right: -2,
                              width: 17,
                              height: 17,
                              borderRadius: '50%',
                              bgcolor: isDark ? '#242526' : '#FFFFFF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 1,
                              boxShadow: '0 0 0 1.5px ' + (isDark ? '#242526' : '#FFFFFF'),
                            }}
                          >
                            {getChannelIcon(ticket.channel || ticket.whatsapp?.channel)}
                          </Box>
                        </Box>
                      </ListItemDecorator>
                      <ListItemContent sx={{ minWidth: 0 }}>
                        {/* Linea 1: Nombre + Timestamp */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              level="title-sm"
                              noWrap
                              sx={{
                                fontWeight: ticket.unreadMessages > 0 ? 700 : 600,
                                maxWidth: 160,
                              }}
                            >
                              {displayContactName(ticket.contact)}
                            </Typography>
                          </Stack>
                          <Typography
                            level="body-xs"
                            sx={{
                              color: ticket.unreadMessages > 0 ? '#5BC2D2' : 'text.tertiary',
                              fontWeight: ticket.unreadMessages > 0 ? 700 : 400,
                              flexShrink: 0,
                              fontSize: '10px',
                            }}
                          >
                            {formatTicketDate(ticket.updatedAt)}
                          </Typography>
                        </Stack>

                        {/* Linea 2: Ultimo mensaje (o coincidencia al buscar en mensajes) */}
                        {searchMessages && debouncedSearchTerm.trim() && (ticket.matchCount ?? 0) > 0 ? (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ mt: 0.25, pr: 2, minWidth: 0 }}
                          >
                            <Typography
                              level="body-xs"
                              noWrap
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.72rem',
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {highlightText(
                                getMatchSnippet(
                                  ticket.lastMatch?.body || ticket.lastMessage || '',
                                  debouncedSearchTerm.trim()
                                ),
                                debouncedSearchTerm.trim()
                              )}
                            </Typography>
                            <Chip
                              size="sm"
                              variant="soft"
                              color="success"
                              sx={{
                                flexShrink: 0,
                                fontSize: '0.62rem',
                                '--Chip-minHeight': '18px',
                                '--Chip-paddingInline': '6px',
                              }}
                            >
                              {ticket.matchCount} {ticket.matchCount === 1 ? 'mensaje' : 'mensajes'}
                            </Chip>
                          </Stack>
                        ) : (
                          <Typography
                            level="body-xs"
                            noWrap
                            sx={{
                              color: ticket.unreadMessages > 0 ? 'text.primary' : 'text.tertiary',
                              fontWeight: ticket.unreadMessages > 0 ? 600 : 400,
                              mt: 0.25,
                              pr: 2,
                              fontSize: '0.72rem',
                            }}
                          >
                            {formatLastMessagePreview(ticket.lastMessage) || 'Sin mensajes'}
                          </Typography>
                        )}

                        {/* Linea 3: Conexion + Cola + Usuario + Tags + Botones accion */}
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between" sx={{ mt: 0.45, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.4} alignItems="center" useFlexGap sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            {resolvedWhatsapp && (
                              <Chip
                                size="sm"
                                variant="soft"
                                sx={{
                                  bgcolor: `${getChannelColor(ticket.channel || ticket.whatsapp?.channel)}22`,
                                  color: getChannelColor(ticket.channel || ticket.whatsapp?.channel),
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  height: 16,
                                  px: 0.5,
                                  maxWidth: 88,
                                  flexShrink: 1,
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              >
                                {resolvedWhatsapp.name}
                              </Chip>
                            )}
                            {resolvedQueue && (
                              <Chip
                                size="sm"
                                sx={{
                                  bgcolor: resolvedQueue.color,
                                  color: 'white',
                                  fontSize: '0.6rem',
                                  height: 16,
                                  px: 0.5,
                                  maxWidth: 72,
                                  flexShrink: 1,
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              >
                                {resolvedQueue.name}
                              </Chip>
                            )}
                            {resolvedUser && (
                              <Chip
                                size="sm"
                                variant="soft"
                                sx={{
                                  fontSize: '0.6rem',
                                  height: 16,
                                  px: 0.5,
                                  maxWidth: 64,
                                  flexShrink: 1,
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              >
                                {resolvedUser.name}
                              </Chip>
                            )}
                            {resolvedTags.slice(0, 2).map((tag) => (
                              <Chip
                                key={tag.id}
                                size="sm"
                                sx={{
                                  bgcolor: tag.color,
                                  color: 'white',
                                  fontSize: '0.55rem',
                                  height: 15,
                                  px: 0.45,
                                  maxWidth: 70,
                                  flexShrink: 1,
                                  border: tag.kanban === 1 ? '1px solid rgba(255,255,255,0.5)' : 'none',
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              >
                                {tag.name}
                              </Chip>
                            ))}
                          </Stack>
                            {/* Botones de accion - visibles en hover */}
                            <Stack
                              className="ticket-actions"
                              direction="row"
                              spacing={0.3}
                              sx={{
                                opacity: selectedTicket?.id === ticket.id ? 1 : 0,
                                transition: 'opacity 0.15s ease',
                                flexShrink: 0,
                              }}
                            >
                              {ticket.status === 'pending' && (
                                <>
                                  <Tooltip title="Aceptar">
                                    <IconButton
                                      size="sm"
                                      variant="soft"
                                      color="success"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleAcceptTicket(ticket)
                                      }}
                                      sx={{ minWidth: 24, minHeight: 24 }}
                                    >
                                      <CheckIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cerrar">
                                    <IconButton
                                      size="sm"
                                      variant="soft"
                                      color="danger"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleCloseTicket(ticket.id)
                                      }}
                                      sx={{ minWidth: 24, minHeight: 24 }}
                                    >
                                      <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              {(ticket.status === 'open' || ticket.status === 'group') && (
                                <Tooltip title="Cerrar">
                                  <IconButton
                                    size="sm"
                                    variant="soft"
                                    color="danger"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCloseTicket(ticket.id)
                                    }}
                                    sx={{ minWidth: 24, minHeight: 24 }}
                                  >
                                    <ClearIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {ticket.status === 'closed' && (
                                <Tooltip title="Reabrir">
                                  <IconButton
                                    size="sm"
                                    variant="soft"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleReopenTicket(ticket)
                                    }}
                                    sx={{ minWidth: 24, minHeight: 24 }}
                                  >
                                    <ReplayIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                        </Stack>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                )
              })
            )}
          </List>

          {/* Loader para paginación infinita — logo ChatEAM con pulso */}
          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                  color: 'text.secondary'
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="ChatEAM"
                  sx={{
                    width: 36,
                    height: 36,
                    objectFit: 'contain',
                    animation: 'chateamPulse 1s ease-in-out infinite',
                    '@keyframes chateamPulse': {
                      '0%, 100%': { transform: 'scale(0.85)', opacity: 0.6 },
                      '50%': { transform: 'scale(1)', opacity: 1 }
                    }
                  }}
                />
                <Typography level="body-xs">Cargando más tickets...</Typography>
              </Box>
            </Box>
          )}

          {/* Scroll sentinel para paginación infinita */}
          {hasMore && tickets.length > 0 && (
            <Box ref={ticketsListRef} sx={{ height: 1, mt: 1 }} />
          )}
        </Sheet>
      </Box>

      {/* Chat Area */}
      {selectedTicket ? (
        <Box
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, position: 'relative' }}
        >
          {isChatDraggingFiles && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 30,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDark ? 'rgba(11,20,26,0.62)' : 'rgba(255,255,255,0.62)',
                border: '2px dashed',
                borderColor: '#5BC2D2',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 'sm',
                  bgcolor: 'background.surface',
                  boxShadow: 'md',
                }}
              >
                <Typography level="title-sm">Suelta para adjuntar</Typography>
              </Box>
            </Box>
          )}
          {/* Campaign Banner - Muestra info de campaña O selector para asignar */}
          {campaignMessage ? (
            // ✅ HAY campaña asignada - Mostrar info + conversión
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'stretch',
                borderBottom: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              })}
            >
              {/* Sección Anuncio - Azul */}
              <Tooltip
                variant="outlined"
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography level="body-xs" fontWeight="lg" sx={{ color: 'primary.600', mb: 0.5 }}>
                      Detalles del Anuncio
                    </Typography>
                    {getCampaignMessageCampaignName(campaignMessage) && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Campaña: {getCampaignMessageCampaignName(campaignMessage)}</Typography>
                    )}
                    {campaignMessage.headline && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Título: {campaignMessage.headline}</Typography>
                    )}
                    {campaignMessage.sourceId && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Ad ID: {campaignMessage.sourceId}</Typography>
                    )}
                    {campaignMessage.ctwaClid && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>CTWA: {campaignMessage.ctwaClid.substring(0, 20)}...</Typography>
                    )}
                    {campaignMessage.rawData?.manuallyAssigned && (
                      <Typography level="body-xs" sx={{ color: 'warning.500', fontStyle: 'italic' }}>Asignación manual</Typography>
                    )}
                  </Box>
                }
                placement="bottom-start"
              >
                <Box
                  sx={(theme) => ({
                    px: 1.5,
                    py: 0.75,
                    bgcolor: theme.palette.mode === 'dark' ? '#1C3A3F' : '#E8F8FA',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    borderRight: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? '#5BC2D2' : '#9FE1EB',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? '#3B8E9A' : '#C5EDF3',
                    },
                  })}
                >
                  <Typography sx={{ fontSize: 14 }}>📢</Typography>
                  <Typography level="body-xs" fontWeight="lg" sx={(theme) => ({ color: theme.palette.mode === 'dark' ? '#9FE1EB' : '#3B8E9A' })}>
                    {formatCompactCampaignName(campaignMessage)}
                  </Typography>
                  {campaignMessage.rawData?.manuallyAssigned && (
                    <Chip size="sm" variant="soft" color="warning" sx={{ height: 16, fontSize: '0.55rem' }}>
                      Manual
                    </Chip>
                  )}
                  {campaignMessage.channel && (
                    <Chip size="sm" variant="solid" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#5BC2D2', color: 'white' }}>
                      {campaignMessage.channel}
                    </Chip>
                  )}
                </Box>
              </Tooltip>

              {/* Sección Conversión - Verde */}
              <Box
                sx={(theme) => ({
                  px: 1.5,
                  py: 0.75,
                  bgcolor: theme.palette.mode === 'dark' ? '#14532d' : '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                })}
              >
                <MoneyIcon sx={(theme) => ({ fontSize: 16, color: theme.palette.mode === 'dark' ? '#86efac' : '#16a34a' })} />
                <Typography level="body-xs" fontWeight="lg" sx={(theme) => ({ color: theme.palette.mode === 'dark' ? '#86efac' : '#15803d', mr: 0.5 })}>
                  Conversión:
                </Typography>
                {editingConversion ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                    <Input
                      size="sm"
                      placeholder="ej: $100"
                      value={conversionNoteValue}
                      onChange={(e) => setConversionNoteValue(e.target.value)}
                      sx={{ flex: 1, maxWidth: 140, '--Input-minHeight': '26px', fontSize: '0.75rem' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveConversionNote()
                        if (e.key === 'Escape') { setEditingConversion(false); setConversionNoteValue(campaignMessage?.conversionNote || '') }
                      }}
                      autoFocus
                    />
                    <IconButton size="sm" variant="solid" color="success" onClick={handleSaveConversionNote} loading={savingConversion} sx={{ minWidth: 26, minHeight: 26 }}>
                      <SaveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="sm" variant="soft" color="neutral" onClick={() => { setEditingConversion(false); setConversionNoteValue(campaignMessage?.conversionNote || '') }} sx={{ minWidth: 26, minHeight: 26 }}>
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    {campaignMessage.conversionNote ? (
                      <Chip size="sm" variant="solid" sx={{ height: 22, fontSize: '0.75rem', fontWeight: 700, bgcolor: '#16a34a', color: 'white' }}>
                        {campaignMessage.conversionNote}
                      </Chip>
                    ) : (
                      <Typography level="body-xs" sx={(theme) => ({ color: theme.palette.mode === 'dark' ? '#6b7280' : '#9ca3af', fontStyle: 'italic' })}>
                        Sin valor
                      </Typography>
                    )}
                    <Tooltip title="Editar conversión">
                      <IconButton size="sm" variant="soft" color="success" onClick={() => setEditingConversion(true)} sx={{ minWidth: 24, minHeight: 24 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Box>
            </Box>
          ) : (
            // ❌ NO hay campaña - Mostrar selector para asignar
            <Box
              sx={(theme) => ({
                px: 2,
                py: 1,
                bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9',
                borderBottom: '1px solid',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              })}
            >
              <Typography level="body-xs" fontWeight="lg" sx={{ color: 'text.secondary' }}>
                📢 Asignar a campaña:
              </Typography>

              {/* Select de Campañas */}
              <Select
                size="sm"
                placeholder="Seleccionar campaña..."
                value={selectedCampaignId}
                onChange={(_, value) => {
                  setSelectedCampaignId(value as string)
                  setSelectedAdId('')
                }}
                sx={{ minWidth: 200, '--Select-minHeight': '28px' }}
                slotProps={{ button: { sx: { fontSize: '0.75rem' } } }}
                onListboxOpenChange={(isOpen) => {
                  if (isOpen && campaigns.length === 0) fetchCampaigns()
                }}
              >
                {loadingCampaigns ? (
                  <Option value="" disabled>Cargando campañas...</Option>
                ) : campaigns.length === 0 ? (
                  <Option value="" disabled>No hay campañas disponibles</Option>
                ) : (
                  campaigns.map(campaign => (
                    <Option key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </Option>
                  ))
                )}
              </Select>

              {/* Select de Anuncios (aparece cuando hay campaña seleccionada) */}
              {selectedCampaignId && (
                <Select
                  size="sm"
                  placeholder="Seleccionar anuncio..."
                  value={selectedAdId}
                  onChange={(_, value) => setSelectedAdId(value as string)}
                  sx={{ minWidth: 200, '--Select-minHeight': '28px' }}
                  slotProps={{ button: { sx: { fontSize: '0.75rem' } } }}
                >
                  {loadingAds ? (
                    <Option value="" disabled>Cargando anuncios...</Option>
                  ) : ads.length === 0 ? (
                    <Option value="" disabled>No hay anuncios en esta campaña</Option>
                  ) : (
                    ads.map(ad => (
                      <Option key={ad.id} value={ad.id}>
                        {ad.name}
                      </Option>
                    ))
                  )}
                </Select>
              )}

              {/* Botón Asignar (aparece cuando hay anuncio seleccionado) */}
              {selectedAdId && (
                <Button
                  size="sm"
                  color="primary"
                  variant="solid"
                  loading={assigning}
                  onClick={handleAssignCampaign}
                  sx={{ '--Button-minHeight': '28px' }}
                >
                  Asignar
                </Button>
              )}
            </Box>
          )}


          {/* Chat Header - Messenger Style */}
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              bgcolor: 'background.surface',
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                {/* Ampliar / volver a la lista. En movil hace de boton "atras". */}
                <Tooltip title={chatAmpliado ? 'Mostrar lista de chats' : 'Ampliar conversacion'} placement="bottom">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setChatAmpliado(v => !v)}
                    sx={{ flexShrink: 0, mr: 0.25 }}
                    aria-label={chatAmpliado ? 'Mostrar lista de chats' : 'Ampliar conversacion'}
                  >
                    {chatAmpliado
                      ? (esMovil ? <ArrowBackIcon fontSize="small" /> : <FullscreenExitIcon fontSize="small" />)
                      : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {selectedTicket.isGroup ? (
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <GroupIcon />
                  </Avatar>
                ) : (
                  <Avatar src={selectedTicket.contact?.profilePicUrl} sx={{ width: 32, height: 32 }}>
                    {contactAvatarInitial(selectedTicket.contact)}
                  </Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography level="title-sm" noWrap sx={{ fontWeight: 700, lineHeight: 1.15, fontSize: '0.9rem' }}>
                    {displayContactName(selectedTicket.contact)}
                  </Typography>
                  {displayContactSubtitle(selectedTicket.contact) && (
                    <Typography
                      level="body-xs"
                      noWrap
                      sx={{ color: 'text.tertiary', fontSize: '10px', lineHeight: 1.1 }}
                    >
                      {displayContactSubtitle(selectedTicket.contact)}
                    </Typography>
                  )}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      flexWrap: 'nowrap',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}
                  >
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                      bgcolor: selectedTicket.status === 'open' ? '#31A24C' : '#8A8D91',
                    }} />
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {selectedTicket.status === 'open' ? 'Activo' : getStatusLabel(selectedTicket.status)}
                    </Typography>
                    {/* FASE 1 Coexistencia — chip de canal actual del ticket */}
                    {selectedTicket.channel && (
                      <ChannelBadge
                        provider={
                          selectedTicket.channel === 'whatsapp'
                            ? 'baileys'
                            : (selectedTicket.channel as any)
                        }
                        isDark={isDark}
                        compact
                      />
                    )}
                    {/* FASE 5 Coexistencia — selector dinámico de routing saliente */}
                    {selectedTicket.id && (selectedTicket.channel === 'whatsapp' || selectedTicket.channel === 'meta') && (
                      <RoutingPolicySelector
                        ticketId={selectedTicket.id}
                        ticketChannel={selectedTicket.channel}
                        isDark={isDark}
                      />
                    )}
                    {/* FASE 7 Coexistencia — indicador ventana 24h Meta + botón Pasar a WhatsApp */}
                    {selectedTicket.id && (selectedTicket.channel === 'whatsapp' || selectedTicket.channel === 'meta') && (
                      <MetaWindowIndicator
                        ticketId={selectedTicket.id}
                        ticketChannel={selectedTicket.channel}
                        isDark={isDark}
                      />
                    )}
                    {(() => {
                      const headerQueue = getResolvedQueue(selectedTicket)
                      return headerQueue ? (
                        <>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0 }}>·</Typography>
                          <Chip
                            size="sm"
                            sx={{
                              bgcolor: headerQueue.color || '#64748B',
                              color: 'white',
                              height: 16,
                              fontSize: '0.6rem',
                              flexShrink: 0,
                            }}
                          >
                            {headerQueue.name}
                          </Chip>
                        </>
                      ) : null
                    })()}
                    {selectedTicket.user && (
                      <>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0 }}>·</Typography>
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {selectedTicket.user.name}
                        </Typography>
                      </>
                    )}
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0 }}>·</Typography>
                    {customerOriginSelector}
                  </Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {(selectedTicket.channel === 'whatsapp' || selectedTicket.isGroup) && (() => {
                  const cooldownRemainingSeconds = getTicketRecoveryCooldownRemainingSeconds(selectedTicket.id)
                  const isInCooldown = cooldownRemainingSeconds > 0
                  const cooldownMinutes = Math.ceil(cooldownRemainingSeconds / 60)
                  const tooltipTitle = isInCooldown
                    ? `Espera ${cooldownMinutes} min para volver a recuperar`
                    : 'Recuperar mensajes faltantes'

                  return (
                    <Tooltip title={tooltipTitle}>
                      <span>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="warning"
                          loading={recoveringMessages}
                          disabled={isInCooldown}
                          onClick={handleRecoverTicketMessages}
                          sx={{
                            borderRadius: '50%',
                            '&:hover': { bgcolor: isDark ? '#3A3B3C' : '#E4E6EB' },
                          }}
                        >
                          <RefreshIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                })()}
                {selectedTicket.status !== 'closed' && (
                  <Tooltip title="Cerrar ticket">
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => handleCloseTicket()}
                      sx={{
                        borderRadius: '50%',
                        '&:hover': { bgcolor: isDark ? '#3A3B3C' : '#E4E6EB' },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Ver contacto">
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setContactDrawerOpen(true)}
                    sx={{
                      borderRadius: '50%',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: isDark ? '#3A3B3C' : '#E4E6EB', color: '#5BC2D2' },
                    }}
                  >
                    <ContactIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Más opciones">
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={(e) => setTicketMoreMenu(e.currentTarget)}
                    sx={{
                      borderRadius: '50%',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: isDark ? '#3A3B3C' : '#E4E6EB' },
                    }}
                  >
                    <MoreIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Menu
                  open={Boolean(ticketMoreMenu)}
                  anchorEl={ticketMoreMenu}
                  onClose={() => setTicketMoreMenu(null)}
                  placement="bottom-end"
                >
                  <MenuItem onClick={() => {
                    setTicketMoreMenu(null)
                    setContactDrawerOpen(true)
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ContactIcon sx={{ fontSize: 18 }} />
                      <Typography level="body-sm">Ver contacto</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    setTicketMoreMenu(null)
                    setShowTransferModal(true)
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TransferIcon sx={{ fontSize: 18 }} />
                      <Typography level="body-sm">Transferir ticket</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    color="danger"
                    onClick={async () => {
                      setTicketMoreMenu(null)
                      if (!selectedTicket) return
                      const confirmed = window.confirm('¿Estás seguro de eliminar este ticket? Esta acción no se puede deshacer.')
                      if (!confirmed) return
                      try {
                        await api.delete(`/tickets/${selectedTicket.id}`)
                        toast.success('Ticket eliminado correctamente')
                        setSelectedTicket(null)
                        // El socket `company-X-ticket` con action='delete' remueve
                        // el ticket de la lista sin recargar (preserva scroll).
                        fetchTicketCounts()
                      } catch (error: any) {
                        console.error('Error al eliminar ticket:', error)
                        toast.error(error.response?.data?.message || 'Error al eliminar el ticket')
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DeleteIcon sx={{ fontSize: 18, color: 'var(--joy-palette-danger-500)' }} />
                      <Typography level="body-sm" color="danger">Eliminar ticket</Typography>
                    </Box>
                  </MenuItem>
                </Menu>
                <Tooltip title="Buscar en conversación">
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setConversationSearchActive(!conversationSearchActive)}
                    sx={{
                      borderRadius: '50%',
                      color: conversationSearchActive ? '#5BC2D2' : 'text.secondary',
                      '&:hover': { bgcolor: isDark ? '#3A3B3C' : '#E4E6EB' },
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {/* Barra de búsqueda en conversación */}
          {conversationSearchActive && (
            <ConversationSearchBar
              messages={selectedTicket?.messages || []}
              searchTerm={conversationSearchTerm}
              onSearchTermChange={setConversationSearchTerm}
              isDark={isDark}
            />
          )}

          {/* FASE 6 Coexistencia — "Trazabilidad de envíos (coexistencia)" OCULTO temporalmente (a pedido del usuario).
              Para reactivar: descomentar el bloque siguiente.
          {selectedTicket.id && (selectedTicket.channel === 'whatsapp' || selectedTicket.channel === 'meta') && (
            <Box sx={{ px: 2, pt: 1 }}>
              <DispatchTimeline
                ticketId={selectedTicket.id}
                ticketChannel={selectedTicket.channel}
                isDark={isDark}
              />
            </Box>
          )}
          */}

          {/* Messages */}
          <Box
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: { xs: 1, md: 1.5 },
              py: { xs: 0.75, md: 1 },
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              ...getChatBackgroundSx(isDark),
              ...thinScrollbarSx,
            }}
          >
            <Stack spacing={0.25} sx={{ position: 'relative', zIndex: 1, marginTop: 'auto' }}>
              {loadingMessages && selectedTicket.messages.length === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, py: 4 }}>
                  <CircularProgress size="sm" sx={{ '--CircularProgress-size': '28px' }} />
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    Cargando mensajes...
                  </Typography>
                </Box>
              )}
              {loadingMoreMessages && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                    Cargando mensajes anteriores...
                  </Typography>
                </Box>
              )}
              {selectedTicket.messages.map((msg, index) => {
                const isOwn = msg.fromMe
                // Rol de color de la burbuja. Invertimos el énfasis: los mensajes
                // del CLIENTE (no propios) llevan el color destacado (azul/teal) y
                // los propios el gris. `isOwn` se sigue usando para la POSICIÓN
                // (izq/der), la cola de la burbuja y la visibilidad de los checks.
                const emphasized = !isOwn
                const prevMsg = index > 0 ? selectedTicket.messages[index - 1] : null
                const showSeparator = shouldShowDateSeparator(msg, prevMsg)
                const isMessageDeleted = msg.isDeleted || msg.messageStatus === 'deleted'
                const isBeingEdited = editingMessageId === msg.id
                const isMetaMessage = selectedTicket.channel === 'tiktok' && !isOwn && msg.dataJson
                const canSelectCurrentMessage = canSelectMessageForMode(msg)

                if (msg.isPrivate && !isOwn) return null

                return (
                  <Box key={msg.id} id={`msg-${msg.id}`}>
                    {showSeparator && (
                      <DateSeparator
                        label={formatDateSeparator(msg.createdAt)}
                        isDark={isDark}
                      />
                    )}

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: isOwn ? 'flex-end' : 'flex-start',
                        mb: 0.25,
                        position: 'relative',
                        '&:hover .message-actions': {
                          opacity: 1,
                        },
                      }}
                      onClick={() => {
                        if (selectionMode && canSelectCurrentMessage) {
                          handleToggleMessageSelection(msg)
                        }
                      }}
                    >
                      {/* CHECKBOX en modo selección */}
                      {selectionMode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
                          <Checkbox
                            checked={selectedMessageIds.has(msg.id)}
                            disabled={!canSelectCurrentMessage}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => handleToggleMessageSelection(msg)}
                            size="sm"
                          />
                        </Box>
                      )}

                      {/* ─── MODO EDICIÓN ─── */}
                      {isBeingEdited ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '65%' }}>
                          <Input
                            value={editingMessageBody}
                            onChange={(e) => setEditingMessageBody(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEditMessage()
                              if (e.key === 'Escape') handleCancelEdit()
                            }}
                            autoFocus
                            sx={{ flex: 1, fontSize: '0.875rem' }}
                            disabled={savingEdit}
                          />
                          <IconButton size="sm" variant="solid" color="primary"
                            onClick={handleSaveEditMessage} loading={savingEdit}
                            disabled={!editingMessageBody.trim()}>
                            <CheckIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="sm" variant="soft" color="neutral"
                            onClick={handleCancelEdit} disabled={savingEdit}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      ) : (
                        /* ─── MODO NORMAL / ELIMINADO ─── */
                        <>
                          {/* TikTok special */}
                          {isMetaMessage ? (
                            <TikTokCommentBubble message={msg} />
                          ) : (
                            <Box
                              sx={{
                                maxWidth: { xs: '92%', sm: '82%', md: '74%' },
                                bgcolor: isMessageDeleted
                                  ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')
                                  : (emphasized
                                      ? (isDark
                                          ? facebookDesignTokens.message.outgoing.background
                                          : facebookDesignTokens.message.outgoing.backgroundLight)
                                      : (isDark
                                          ? facebookDesignTokens.message.incoming.background
                                          : facebookDesignTokens.message.incoming.backgroundLight)),
                                color: isMessageDeleted ? 'text.disabled' : (emphasized
                                    ? (isDark
                                        ? facebookDesignTokens.message.outgoing.color
                                        : facebookDesignTokens.message.outgoing.colorLight)
                                    : (isDark
                                        ? facebookDesignTokens.message.incoming.color
                                        : facebookDesignTokens.message.incoming.colorLight)),
                                px: { xs: 1, md: 1.25 },
                                py: { xs: 0.5, md: 0.75 },
                                borderRadius: facebookDesignTokens.message.borderRadius,
                                borderTopRightRadius: isOwn ? '4px' : facebookDesignTokens.message.borderRadius,
                                borderTopLeftRadius: !isOwn ? '4px' : facebookDesignTokens.message.borderRadius,
                                boxShadow: isDark ? '0 1px 0.5px rgba(11,20,26,.13)' : '0 1px 0.5px rgba(0,0,0,.08)',
                                opacity: isMessageDeleted ? 0.6 : 1,
                                position: 'relative',
                                '& a': {
                                  color: emphasized ? 'inherit !important' : '#1877F2',
                                  textDecorationColor: 'currentColor',
                                },
                                '& a:visited': {
                                  color: emphasized ? 'inherit !important' : '#6B46C1',
                                },
                                '& a:hover': {
                                  color: emphasized ? 'inherit !important' : '#0F5EC7',
                                  textDecorationColor: 'currentColor',
                                },
                              }}
                            >
                              {/* Body — badge "Mensaje eliminado" (si aplica) + el contenido SIEMPRE visible.
                                  ChatEAM: como servicio de mensajería conservamos y mostramos el contenido
                                  original aunque el mensaje haya sido eliminado por el contacto. */}
                              {isMessageDeleted && (
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                                  <DeleteIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
                                    Mensaje eliminado
                                  </Typography>
                                </Stack>
                              )}
                              {(
                                <MessageContent
                                  message={{
                                    ...msg,
                                    // Placeholder optimista: la mediaUrl ya es un blob: local, NO anteponer backend
                                    mediaUrl: (msg as { isUploading?: boolean }).isUploading
                                      ? msg.mediaUrl
                                      : msg.mediaUrl
                                        ? `${BACKEND_URL}/public/company${user?.companyId}/${msg.mediaUrl}`
                                        : msg.mediaUrl,
                                  }}
                                  isDark={isDark}
                                  isOwn={emphasized}
                                  isGroup={selectedTicket?.isGroup}
                                  searchTerm={conversationSearchActive ? conversationSearchTerm : undefined}
                                  onLightboxOpen={(src, type, currentIndex, allMedia) =>
                                    setLightboxData({ src, type, currentIndex: currentIndex as number, allMedia: (allMedia ?? []) as Array<{id: number; src: string; type: string}> })
                                  }
                                  allMedia={selectedTicket?.messages
                                    ?.filter((m) => m.mediaUrl && !(m as { isUploading?: boolean }).isUploading && (m.mediaType?.toLowerCase().includes('image') || m.mediaType?.toLowerCase().includes('video')))
                                    .map((m) => ({
                                      id: m.id,
                                      src: `${BACKEND_URL}/public/company${user?.companyId}/${m.mediaUrl!}`,
                                      type: m.mediaType?.toLowerCase().includes('video') ? 'video' : 'image',
                                    })) || []
                                  }
                                />
                              )}

                              {/* Barra de progreso de subida (UI optimista de media) */}
                              {(msg as { isUploading?: boolean }).isUploading && (
                                <LinearProgress
                                  determinate={(uploadProgress[String(msg.id)] ?? 0) > 0}
                                  value={uploadProgress[String(msg.id)] ?? 0}
                                  sx={{
                                    mt: 0.5,
                                    '--LinearProgress-thickness': '4px',
                                    '--LinearProgress-radius': '4px',
                                    color: emphasized ? 'rgba(255,255,255,0.85)' : '#5BC2D2',
                                  }}
                                />
                              )}

                              {/* Footer: indicadores + tiempo */}
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.25, position: 'relative' }}>
                                {(msg as { isUploading?: boolean }).isUploading ? (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <CircularProgress
                                      size="sm"
                                      determinate={(uploadProgress[String(msg.id)] ?? 0) > 0}
                                      value={uploadProgress[String(msg.id)] ?? 0}
                                      sx={{ '--CircularProgress-size': '14px', '--CircularProgress-trackThickness': '2px', '--CircularProgress-progressThickness': '2px', color: emphasized ? 'rgba(255,255,255,0.8)' : '#5BC2D2' }}
                                    />
                                    <Typography sx={{ fontSize: '11px', color: emphasized ? 'rgba(255,255,255,0.75)' : (isDark ? '#8A8D91' : 'rgba(5,5,5,0.55)'), userSelect: 'none' }}>
                                      Enviando… {uploadProgress[String(msg.id)] ?? 0}%
                                    </Typography>
                                  </Stack>
                                ) : (
                                <>
                                {msg.isEdited && !isMessageDeleted && (
                                  <Typography sx={{ fontSize: '10px', color: 'text.disabled', fontStyle: 'italic' }}>
                                    editado
                                  </Typography>
                                )}
                                {msg.isForwarded && !isMessageDeleted && (
                                  <Typography sx={{ fontSize: '10px', color: 'text.disabled' }}>
                                    reenviado
                                  </Typography>
                                )}
                                <Typography sx={{ fontSize: '11px', color: emphasized ? 'rgba(255,255,255,0.6)' : (isDark ? '#8A8D91' : 'rgba(5,5,5,0.45)'), userSelect: 'none' }}>
                                  {formatTime(msg.createdAt)}
                                </Typography>
                                {/* FASE 1 Coexistencia — badge de canal físico (Meta/Baileys/business_app).
                                    Se renderiza sólo si el backend envía provider o sourceChannel. */}
                                {!isMessageDeleted && (
                                  <ChannelBadge
                                    provider={(msg as any).provider}
                                    sourceChannel={(msg as any).sourceChannel}
                                    isDark={isDark}
                                    compact
                                  />
                                )}
                                {isOwn && msg.ack !== undefined && !isMessageDeleted && (
                                  <Typography sx={{ fontSize: '11px', color: msg.ack >= 3 ? '#5BC2D2' : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.35)') }}>
                                    {msg.ack === 0 ? '🕐' : msg.ack >= 2 ? '✓✓' : '✓'}
                                    {msg.ack === 4 && <span style={{ marginLeft: 2 }}>▶</span>}
                                  </Typography>
                                )}

                                {/* BOTÓN MENÚ (hover) — anclado al footer, abajo-right */}
                                {!isMessageDeleted && !selectionMode && (
                                  <Box
                                    className="message-actions"
                                    onClick={(e) => handleOpenMessageMenu(e, msg.id, isOwn)}
                                    sx={{
                                      position: 'absolute',
                                      right: -20,
                                      bottom: -4,
                                      opacity: 0,
                                      transition: 'opacity 0.15s',
                                      bgcolor: isDark ? '#2d2d2d' : '#e4e6eb',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 24,
                                      height: 24,
                                      boxShadow: 1,
                                      cursor: 'pointer',
                                      zIndex: 2,
                                      '&:hover': { opacity: 1 },
                                    }}
                                  >
                                    <MoreIcon sx={{ fontSize: 14 }} />
                                  </Box>
                                )}
                                </>
                                )}
                              </Stack>
                            </Box>
                          )}

                          {/* MENÚ DE ACCIONES */}
                          {messageActionMenu?.messageId === msg.id && (
                            <Menu
                              anchorEl={messageActionMenu.anchorEl}
                              open={true}
                              onClose={handleCloseMessageMenu}
                              placement="top-start"
                              size="sm"
                            >
                              {(() => {
                                const media = getDownloadableMedia(msg)
                                if (!media) return null
                                return (
                                  <MenuItem
                                    component="a"
                                    href={media.url}
                                    download={media.name}
                                    onClick={() => handleCloseMessageMenu()}
                                  >
                                    <ListItemDecorator><DownloadIcon /></ListItemDecorator>
                                    Descargar
                                  </MenuItem>
                                )
                              })()}
                              <MenuItem onClick={() => {
                                const msgToReply = selectedTicket?.messages.find((m) => m.id === messageActionMenu?.messageId)
                                if (msgToReply) setReplyingTo(msgToReply)
                                setMessageActionMenu(null)
                              }}>
                                <ListItemDecorator><ReplyIcon /></ListItemDecorator>
                                Responder
                              </MenuItem>
                              {canSelectMessageForMode(msg, 'forward') && (
                                <MenuItem onClick={() => { handleCloseMessageMenu(); handleEnterSelectionMode('forward', msg); }}>
                                  <ListItemDecorator sx={{ transform: 'scaleX(-1)' }}><ReplyIcon /></ListItemDecorator>
                                  Reenviar
                                </MenuItem>
                              )}
                              {isOwn && (
                                <MenuItem onClick={() => { handleCloseMessageMenu(); handleStartEditMessage(msg.id, msg.body || ''); }}>
                                  <ListItemDecorator><EditIcon /></ListItemDecorator>
                                  Editar
                                </MenuItem>
                              )}
                              {isOwn && (
                                <MenuItem
                                  onClick={() => { handleCloseMessageMenu(); handleEnterSelectionMode('delete', msg); }}
                                >
                                  <ListItemDecorator><DeleteIcon /></ListItemDecorator>
                                  Eliminar
                                </MenuItem>
                              )}
                            </Menu>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )
              })}
              <div ref={messagesEndRef} />
            </Stack>
          </Box>

          {/* Message Input */}
          <MessageInput
            ticketId={selectedTicket.id}
            ticketStatus={selectedTicket.status}
            ticketChannel={selectedTicket.channel === 'tiktok' ? 'tiktok' : selectedTicket.isGroup ? 'group' : (selectedTicket.channel || 'whatsapp')}
            droppedFiles={dragDropFiles}
            onDroppedFilesHandled={handleDroppedFilesHandled}
            contactId={selectedTicket.contact?.id}
            contactName={displayContactName(selectedTicket.contact)}
            contactNumber={selectedTicket.contact?.number}
            whatsappId={selectedTicket.whatsappId ?? selectedTicket.whatsapp?.id}
            whatsappName={selectedTicket.whatsapp?.name}
            replyingTo={replyingTo || undefined}
            onCancelReply={() => setReplyingTo(null)}
            onSendMessage={(msg) => {
              setReplyingTo(null)
              setSelectedTicket((prev) => prev ? {
                ...prev,
                lastMessage: msg,
                updatedAt: new Date().toISOString(),
              } : null)
            }}
            onMediaSendStart={handleMediaSendStart}
            onMediaSendProgress={handleMediaSendProgress}
            onMediaSendEnd={handleMediaSendEnd}
          />

          {/* Media Lightbox */}
          {lightboxData && (
            <MediaLightbox
              open={true}
              onClose={() => setLightboxData(null)}
              currentSrc={lightboxData.src}
              currentType={lightboxData.type}
              currentIndex={lightboxData.currentIndex}
              allMedia={lightboxData.allMedia}
              isDark={isDark}
              onNavigate={(src, type, idx) =>
                setLightboxData((prev) => prev ? { ...prev, src, type, currentIndex: idx } : null)
              }
            />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            ...getChatBackgroundSx(isDark),
          }}
        >
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            {/* Circulo decorativo con icono */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: isDark ? 'rgba(91,194,210,0.1)' : 'rgba(91,194,210,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <MessageIcon sx={{ fontSize: 36, color: '#5BC2D2', opacity: 0.7 }} />
            </Box>
            <Box
              component="img"
              src="/chateam-logo.png"
              alt="Chateam"
              sx={{ width: 200, height: 'auto', mb: 2, opacity: 0.35 }}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <Typography level="title-lg" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
              Selecciona una conversacion
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary', maxWidth: 300, mx: 'auto', lineHeight: 1.6 }}>
              Elige un ticket de la lista para ver los mensajes y comenzar a chatear
            </Typography>
            <Typography level="body-xs" sx={{ color: '#5BC2D2', opacity: 0.5, mt: 3 }}>
              Powered by Chateam
            </Typography>
          </Box>
        </Box>
      )}

      {/* Contact Drawer */}
      <ContactDrawer
        open={contactDrawerOpen}
        onClose={() => setContactDrawerOpen(false)}
        contact={selectedTicket?.contact || null}
        ticket={selectedTicket}
        loading={loading}
        onContactPatched={handleContactPatchedFromDrawer}
        onTicketPatched={handleTicketPatchedFromDrawer}
      />

      {/* ─── MODAL ACEPTAR TICKET (selección de cola obligatoria si falta) ─── */}
      <Modal open={confirmarCierreMasivo} onClose={() => setConfirmarCierreMasivo(false)}>
        <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 440 }}>
          <Typography level="h4">
            Cerrar todos los tickets {statusFilter === 'open' ? 'abiertos' : 'pendientes'}
          </Typography>
          <Typography level="body-md" sx={{ mt: 1 }}>
            Se cerrarán <strong>{statusFilter === 'open' ? openCount : pendingCount}</strong> tickets
            de tu empresa. No se enviará mensaje de despedida.
          </Typography>
          <Typography level="body-sm" sx={{ mt: 1, color: 'danger.500' }}>
            Esta acción no se puede deshacer.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setConfirmarCierreMasivo(false)}
              disabled={cerrandoTodos}
            >
              Cancelar
            </Button>
            <Button color="danger" loading={cerrandoTodos} onClick={cerrarTodosLosTickets}>
              Sí, cerrar todos
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <AcceptTicketModal
        open={acceptQueueModalOpen}
        onClose={handleAcceptQueueClose}
        queues={currentUserQueues}
        ticketId={ticketToAccept?.id ?? null}
        contactName={ticketToAccept?.contact ? displayContactName(ticketToAccept.contact) : undefined}
        submitting={acceptingTicket}
        errorMessage={acceptError}
        onConfirm={handleAcceptQueueConfirm}
      />

      {/* ─── BARRA DE SELECCIÓN ─── */}
      {selectionMode && (
        <ForwardSelectionBar
          mode={selectionMode}
          selectedCount={selectedMessageIds.size}
          onCancel={handleExitSelectionMode}
          onAction={selectionMode === 'delete' ? handleDeleteMessages : () => setShowForwardModal(true)}
          loading={actionLoading}
        />
      )}

      {/* ─── MODAL REENVÍO ─── */}
      <ForwardContactPicker
        open={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        onForward={handleForwardMessages}
        loading={forwardLoading}
        whatsappId={selectedTicket?.whatsappId}
        channel={selectedTicket?.channel}
      />

      {/* ─── MODAL NUEVO TICKET ─── */}
      <Modal
        open={showNewTicketModal}
        onClose={() => {
          setShowNewTicketModal(false)
          setNewTicketContactSearch('')
          setNewTicketContacts([])
          setNewTicketContactId(null)
          setNewTicketQueueId('')
          setNewTicketWhatsappId('')
        }}
      >
        <ModalDialog sx={{ minWidth: { xs: 0, sm: 'min(420px, calc(100vw - 32px))' }, maxWidth: 500 }}>
          <Typography level="title-lg" sx={{ mb: 1 }}>
            Nuevo Ticket
          </Typography>
          <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
            Crea un ticket manualmente seleccionando un contacto existente.
          </Typography>

          {/* Buscar contacto */}
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Contacto *</FormLabel>
            <Autocomplete
              placeholder="Busca por nombre o número..."
              inputValue={newTicketContactSearch}
              onInputChange={(_event, newInputValue) => {
                handleContactSearchChange(newInputValue)
              }}
              onChange={(_event, newValue: any) => {
                setNewTicketContactId(newValue?.id || null)
              }}
              loading={searchingNewTicketContacts}
              options={newTicketContacts}
              getOptionLabel={(option: any) => `${option.name} - ${option.number}`}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              filterOptions={(options) => options}
              renderOption={(props: any, option: any) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {option.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography level="body-sm">{option.name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {option.number}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            />
          </FormControl>

          {/* Cola */}
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Cola</FormLabel>
            <Select
              value={newTicketQueueId}
              onChange={(_, value) => setNewTicketQueueId(value as string)}
              size="sm"
              placeholder="Selecciona una cola (opcional)"
            >
              <Option value="">Sin cola</Option>
              {queues.map((queue) => (
                <Option key={queue.id} value={queue.id.toString()}>
                  {queue.name}
                </Option>
              ))}
            </Select>
          </FormControl>

          {/* Conexión WhatsApp */}
          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Conexión</FormLabel>
            <Select
              value={newTicketWhatsappId}
              onChange={(_, value) => setNewTicketWhatsappId(value as string)}
              size="sm"
              placeholder="Selecciona conexión (opcional)"
            >
              <Option value="">Predeterminada</Option>
              {whatsapps
                .filter((w) => w.channel === 'whatsapp' || w.channel === 'meta')
                .map((w) => (
                  <Option key={w.id} value={w.id.toString()}>
                    {w.name}
                  </Option>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => {
                setShowNewTicketModal(false)
                setNewTicketContactSearch('')
                setNewTicketContacts([])
                setNewTicketContactId(null)
                setNewTicketQueueId('')
                setNewTicketWhatsappId('')
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateNewTicket}
              loading={newTicketLoading}
              disabled={!newTicketContactId}
              color="primary"
            >
              Crear Ticket
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      {/* ─── MODAL TRANSFERIR TICKET ─── */}
      <Modal
        open={showTransferModal}
        onClose={() => {
          setShowTransferModal(false)
          setSelectedUser(null)
          setSearchUserQuery('')
          setSearchResults([])
        }}
      >
        <ModalDialog sx={{ minWidth: { xs: 0, sm: 'min(400px, calc(100vw - 32px))' }, maxWidth: 500 }}>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Transferir Ticket
          </Typography>
          <Typography level="body-sm" sx={{ mb: 3, color: 'text.secondary' }}>
            Selecciona el agente al que se reasignará este ticket. El responsable cambiará al usuario seleccionado.
          </Typography>

          <FormControl sx={{ mb: 3 }}>
            <FormLabel>Buscar usuario</FormLabel>
            <Autocomplete
              placeholder="Escribe el nombre o email del agente..."
              value={selectedUser}
              onChange={(_event, newValue) => {
                setSelectedUser(newValue)
              }}
              inputValue={searchUserQuery}
              onInputChange={(_event, newInputValue) => {
                setSearchUserQuery(newInputValue)
                handleSearchUser(newInputValue)
              }}
              loading={searchingUsers}
              options={searchResults}
              getOptionLabel={(option: any) => option?.email ? `${option.name} (${option.email})` : option?.name || ''}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              filterOptions={(options) => options}
              renderOption={(props: any, option: any) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {option.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography level="body-sm">{option.name}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            />
          </FormControl>

          {selectedUser && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
              <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                Usuario seleccionado
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ width: 24, height: 24 }}>
                  {selectedUser.name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography level="body-sm">{selectedUser.name}</Typography>
                  {selectedUser.email && (
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {selectedUser.email}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => {
                setShowTransferModal(false)
                setSelectedUser(null)
                setSearchUserQuery('')
                setSearchResults([])
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleTransferTicket}
              loading={transferring}
              disabled={!selectedUser}
              color="primary"
            >
              Transferir
            </Button>
          </Box>
        </ModalDialog>
      </Modal>

      <Modal
        open={purchaseConfirmModal.open}
        onClose={() => {
          if (!sendingPurchaseConversion) {
            setPurchaseConfirmModal({ open: false, campaignMessage: null, value: 0 })
          }
        }}
      >
        <ModalDialog sx={{ width: 'min(92vw, 460px)' }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MoneyIcon sx={{ color: 'success.500' }} />
              <Typography level="title-lg">
                Enviar Purchase a Meta
              </Typography>
            </Stack>

            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Guardaste un valor de conversión. ¿Quieres enviar ahora la conversión Purchase a Meta Conversions API?
            </Typography>

            <Box sx={{ p: 1.5, bgcolor: 'background.level1', borderRadius: 'sm' }}>
              <Typography level="body-sm" fontWeight="lg">
                Valor: ${purchaseConfirmModal.value} USD
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {purchaseConfirmModal.campaignMessage?.ctwaClid
                  ? 'Se enviará con CTWA click id para atribución exacta.'
                  : 'No hay CTWA click id. Se enviará igual con atribución aproximada usando los datos del contacto.'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                disabled={sendingPurchaseConversion}
                onClick={() => setPurchaseConfirmModal({ open: false, campaignMessage: null, value: 0 })}
              >
                Solo guardar
              </Button>
              <Button
                color="success"
                loading={sendingPurchaseConversion}
                onClick={handleSendPurchaseConversionFromTicket}
              >
                Enviar Purchase
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
    </Box>
    </CssVarsProvider>
  )
}
